import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from "react";
import { Mic, Pause, Play, Square, X, RotateCcw, Check, ClipboardList, Stethoscope, Activity, Pill, FlaskConical, CalendarDays, ShieldCheck, ShoppingBasket, Loader2, FileText, Sparkles, History, AlertTriangle, Trash2, ChevronRight, Pencil, Copy, Minus, Maximize2 } from "lucide-react";
import clsx from "clsx";
import toast from "react-hot-toast";
import useWebSocket, { ReadyState } from "react-use-websocket";

import { EMR_APIS } from "@/api_framework/api_config";
import { useAppSelector } from "@/app_redux/hooks/root_hook";
import { useUpdateTempEMRAPI } from "@/app/hooks/api_hooks/emr/useUpdateTempEMRAPI";
import {
  IVoiceEMRText,
  IVoiceEMRItems,
  IVoiceEMRFollowUp,
  IVoiceEMRLabTestItem,
  IVoiceEMRTreatmentItem,
  IVoiceEMRMedicineItem,
  IVoiceEMRDewormingItem,
  IVoiceEMRFoodItem,
} from "@/app/hooks/api_hooks/emr/useVoiceEMRResultAPI";
import { useConfiguration } from "@/app/hooks/useConfiguration";
import { IEMRUpdatePayloadProps } from "@/api_framework/api_modals/EMRs";
import { IClickLogBody, useClickLogAPI } from "@/app/hooks/api_hooks/org/useClickLogAPI";
import { useVoiceEMRSessionAPI, type IVoiceEMRSessionState } from "@/app/hooks/api_hooks/emr/useVoiceEMRSessionAPI";
import { useVoiceEMRGenerateAPI } from "@/app/hooks/api_hooks/emr/useVoiceEMRGenerateAPI";
import { EditableItemList, ItemSearchAdd, EMR_INPUT_CLASS, type ItemField } from "./ItemEditor";
import { IEMRSectionSearchResponseData } from "@/api_framework/api_modals/EMRs";
import {
  AUDIO_QUEUE_MAX_BYTES,
  clearAudioQueue,
  deleteAudioChunks,
  enqueueAudioChunk,
  getQueueStats,
  peekAudioChunks,
} from "./audioQueue";

const VOICE_SAMPLE_RATE = 16000;

const CONNECT_TIMEOUT_MS = 12000;
const MIC_UNAVAILABLE_STATUS = "Could not access the microphone. Check browser permissions.";

const CLOSE_HINTS: Record<number, string> = {
  1000: "server closed normally",
  1001: "page or server going away",
  1006: "connection dropped without a close frame (network, VPN or proxy)",
  1008: "rejected by the server: expired login or EMR not found",
  1011: "server error while starting the session",
  1015: "TLS failure",
};

const MEDICINE_FIELDS: ItemField[] = [
  "dose", "duration_unit", "frequency", "x_value", "duration", "timing", "quantity", "remark",
];
const SIMPLE_ITEM_FIELDS: ItemField[] = ["quantity", "remark"];
const DEWORMING_FIELDS: ItemField[] = ["quantity"];

const TYPEWRITER_INTERVAL_MS = 18;
const PANEL_WIDTH = "580px";
const PANEL_W = 580;
const MINI_W = 230;
const MINI_H = 84;
const DOCK_GAP = 16;
// Keep at least the header on screen when dragging, so a tall panel can still be
// pulled down (clamping to its full height would pin it near the top).
const HEADER_KEEP_VISIBLE = 48;
// The minimised pill sits a little higher so it clears anything at the very bottom.
const MINI_BOTTOM_GAP = 96;

const clampValue = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

// Open docks top-right; minimised always docks to the bottom-right corner.
const getDockedPosition = (minimised: boolean) =>
  minimised
    ? {
        x: Math.max(DOCK_GAP, window.innerWidth - MINI_W - DOCK_GAP),
        y: Math.max(DOCK_GAP, window.innerHeight - MINI_H - MINI_BOTTOM_GAP),
      }
    : { x: Math.max(DOCK_GAP, window.innerWidth - PANEL_W - DOCK_GAP), y: DOCK_GAP };

type Phase = "idle" | "connecting" | "recording" | "processing" | "ready";

type PreviewTab = "clinical" | "orders";

const SPY_LINE_PX = 72;
const SPY_BOTTOM_PX = 8;
const SPY_CLICK_GAP_PX = 12;
const SPY_RESUME_MS = 700;

type SectionKey =
  | "history"
  | "physical"
  | "assessment"
  | "instructions"
  | "followup"
  | "medications"
  | "lab"
  | "treatments"
  | "deworming"
  | "food";

const SECTION_SCROLL_ANCHORS: Record<SectionKey, string> = {
  history: "medical-instructions",
  physical: "medical-instructions",
  assessment: "medical-instructions",
  instructions: "medical-instructions",
  followup: "follow-up",
  medications: "medicines",
  lab: "lab-tests",
  treatments: "treatments",
  deworming: "deworming",
  food: "food-and-diet",
};

const scrollToEmrSection = (key: SectionKey) => {
  const anchor = SECTION_SCROLL_ANCHORS[key];
  if (!anchor) return;
  const el = document.querySelector<HTMLElement>(`[data-voice-section="${anchor}"]`);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

interface VoiceEMRRecorderProps {
  emrId: string | null;
  emrStatus?: string;
  emrDetails?: IEMRUpdatePayloadProps | null;
  onSuccess: (data: IEMRUpdatePayloadProps) => void;
  isOpen: boolean;
  onClose: () => void;
  onRestore?: () => void;
  autoStart?: boolean;
  /** Continue an in-progress session on arrival instead of prompting. */
  autoResume?: boolean;
  /** Full-height panel (the create-emr page); otherwise it sizes to its content. */
  fullHeight?: boolean;
  onAutoStartConsumed?: () => void;
  onDurationChange?: (seconds: number) => void;
}

interface VoicePreviewState {
  subjective_history: string;
  physical_examinations: string;
  diagnosis: string;
  prognosis: string;
  tentative_diagnosis: string[];
  definitive_diagnosis: string[];
  medical_instructions: string;
  follow_up: {
    follow_up_comment: string;
    follow_up_service: string;
    follow_up_date: string;
  };
  lab_tests: IVoiceEMRLabTestItem[];
  treatments: IVoiceEMRTreatmentItem[];
  treatment_instructions: string;
  medicines: IVoiceEMRMedicineItem[];
  deworming: IVoiceEMRDewormingItem[];
  food_added: IVoiceEMRFoodItem[];
  food_instructions: string;
}

const formatTime = (seconds: number) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

const strOrEmpty = (v: unknown): string => (typeof v === "string" ? v : v == null ? "" : String(v));
const arrOrEmpty = <T,>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : []);

const dateOrNull = (v: unknown): string | null => {
  if (v == null) return null;
  const s = String(v).trim();
  if (!s || s.toLowerCase() === "none") return null;
  return s;
};

const parsePreview = (
  emr_text: IVoiceEMRText | null | undefined,
  emr_items: IVoiceEMRItems | null | undefined,
): VoicePreviewState => {
  const t = emr_text ?? {};
  const items = emr_items ?? {};
  const followRaw = t.follow_up ?? ({} as IVoiceEMRFollowUp);
  const followService = Array.isArray(followRaw.follow_up_service)
    ? followRaw.follow_up_service.join(", ")
    : strOrEmpty(followRaw.follow_up_service);
  return {
    subjective_history: strOrEmpty(t.subjective_history),
    physical_examinations: strOrEmpty(t.physical_examinations),
    diagnosis: strOrEmpty(t.diagnosis),
    prognosis: strOrEmpty(t.prognosis),
    tentative_diagnosis: arrOrEmpty<string>(t.tentative_diagnosis),
    definitive_diagnosis: arrOrEmpty<string>(t.definitive_diagnosis),
    medical_instructions: strOrEmpty(t.medical_instructions),
    follow_up: {
      follow_up_comment: strOrEmpty(followRaw.follow_up_comment),
      follow_up_service: followService,
      follow_up_date: dateOrNull(followRaw.follow_up_date) ?? "",
    },
    lab_tests: arrOrEmpty<IVoiceEMRLabTestItem>(items.lab_test_and_diagnostics),
    treatments: arrOrEmpty<IVoiceEMRTreatmentItem>(items.treatments),
    treatment_instructions: strOrEmpty(items.treatment_instructions),
    medicines: arrOrEmpty<IVoiceEMRMedicineItem>(items.medicines),
    deworming: arrOrEmpty<IVoiceEMRDewormingItem>(items.deworming_meds_treatments),
    food_added: arrOrEmpty<IVoiceEMRFoodItem>(items.food_and_diet?.food_added),
    food_instructions: strOrEmpty(items.food_and_diet?.food_instructions),
  };
};

const normText = (v: string) => v.replace(/\s+/g, " ").trim().toLowerCase();

const mergeText = (base: string, next: string): string => {
  const a = base.trim();
  const b = next.trim();
  if (!b) return base;
  if (!a) return next;
  const na = normText(a);
  const nb = normText(b);
  if (nb.includes(na)) return next;
  if (na.includes(nb)) return base;
  return `${a}\n${b}`;
};

const mergeStringList = (base: string[], next: string[]): string[] => {
  const seen = new Set(base.map(normText));
  return [...base, ...next.filter((v) => v.trim() && !seen.has(normText(v)))];
};

const mergeItemList = <T extends { name?: string }>(base: T[], next: T[], idField: keyof T): T[] => {
  const keyOf = (item: T) => String(item[idField] ?? "").trim() || normText(strOrEmpty(item.name));
  const seen = new Set(base.map(keyOf).filter(Boolean));
  return [...base, ...next.filter((item) => {
    const key = keyOf(item);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  })];
};

const mergeFollowUpField = (base: string, next: string) => (next.trim() ? next : base);

const mergePreview = (base: VoicePreviewState, next: VoicePreviewState): VoicePreviewState => ({
  subjective_history: mergeText(base.subjective_history, next.subjective_history),
  physical_examinations: mergeText(base.physical_examinations, next.physical_examinations),
  diagnosis: mergeText(base.diagnosis, next.diagnosis),
  prognosis: mergeText(base.prognosis, next.prognosis),
  tentative_diagnosis: mergeStringList(base.tentative_diagnosis, next.tentative_diagnosis),
  definitive_diagnosis: mergeStringList(base.definitive_diagnosis, next.definitive_diagnosis),
  medical_instructions: mergeText(base.medical_instructions, next.medical_instructions),
  follow_up: {
    follow_up_comment: mergeText(base.follow_up.follow_up_comment, next.follow_up.follow_up_comment),
    follow_up_service: mergeFollowUpField(base.follow_up.follow_up_service, next.follow_up.follow_up_service),
    follow_up_date: mergeFollowUpField(base.follow_up.follow_up_date, next.follow_up.follow_up_date),
  },
  lab_tests: mergeItemList(base.lab_tests, next.lab_tests, "lab_test_id"),
  treatments: mergeItemList(base.treatments, next.treatments, "treatment_id"),
  treatment_instructions: mergeText(base.treatment_instructions, next.treatment_instructions),
  medicines: mergeItemList(base.medicines, next.medicines, "medicine_id"),
  deworming: mergeItemList(base.deworming, next.deworming, "id"),
  food_added: mergeItemList(base.food_added, next.food_added, "product_id"),
  food_instructions: mergeText(base.food_instructions, next.food_instructions),
});

const previewFromEmrDetails = (
  emrDetails: IEMRUpdatePayloadProps | null | undefined,
): VoicePreviewState => {
  const e = emrDetails ?? {};
  const follow = e.follow_up;
  return {
    subjective_history: strOrEmpty(e.subjective_history),
    physical_examinations: strOrEmpty(e.physical_examinations),
    diagnosis: strOrEmpty(e.diagnosis),
    prognosis: strOrEmpty(e.prognosis),
    tentative_diagnosis: arrOrEmpty<string>(e.tentative_diagnosis),
    definitive_diagnosis: arrOrEmpty<string>(e.definitive_diagnosis),
    medical_instructions: strOrEmpty(e.medical_instructions),
    follow_up: {
      follow_up_comment: strOrEmpty(follow?.follow_up_comment),
      follow_up_service: Array.isArray(follow?.follow_up_service)
        ? follow.follow_up_service.join(", ")
        : strOrEmpty(follow?.follow_up_service),
      follow_up_date: dateOrNull(follow?.follow_up_date) ?? "",
    },
    lab_tests: arrOrEmpty<IVoiceEMRLabTestItem>(e.lab_test_and_diagnostics?.lab_test_added),
    treatments: arrOrEmpty<IVoiceEMRTreatmentItem>(e.treatments?.treatments_added),
    treatment_instructions: strOrEmpty(e.treatments?.treatments_instructions),
    medicines: arrOrEmpty<IVoiceEMRMedicineItem>(e.medicines?.medicines_added),
    deworming: arrOrEmpty<IVoiceEMRDewormingItem>(e.deworming_meds_treatments),
    food_added: arrOrEmpty<IVoiceEMRFoodItem>(e.food_and_diet?.food_added),
    food_instructions: strOrEmpty(e.food_and_diet?.food_instructions),
  };
};

const buildUpdatePayload = (
  preview: VoicePreviewState,
  emrDetails: IEMRUpdatePayloadProps | null | undefined,
  selected: Set<SectionKey> = new Set(),
  skipped: Set<SectionKey> = new Set(),
): IEMRUpdatePayloadProps => {
  const includeAll = selected.size === 0;
  const has = (key: SectionKey) => includeAll || selected.has(key);
  const isSkipped = (key: SectionKey) => skipped.has(key);

  const payload: IEMRUpdatePayloadProps = {
    emr_status: emrDetails?.emr_status,
    remove_children: emrDetails?.remove_children ?? [],
  };

  if (has("history") && (preview.subjective_history || isSkipped("history"))) {
    payload.subjective_history = preview.subjective_history;
  }
  if (has("physical") && (preview.physical_examinations || isSkipped("physical"))) {
    payload.physical_examinations = preview.physical_examinations;
  }
  if (has("assessment")) {
    if (preview.diagnosis || isSkipped("assessment")) payload.diagnosis = preview.diagnosis;
    if (preview.prognosis || isSkipped("assessment")) payload.prognosis = preview.prognosis;
    if (preview.tentative_diagnosis.length || isSkipped("assessment"))
      payload.tentative_diagnosis = preview.tentative_diagnosis;
    if (preview.definitive_diagnosis.length || isSkipped("assessment"))
      payload.definitive_diagnosis = preview.definitive_diagnosis;
  }
  if (has("instructions") && (preview.medical_instructions || isSkipped("instructions"))) {
    payload.medical_instructions = preview.medical_instructions;
  }

  if (has("followup")) {
    const safeFollowUpDate = dateOrNull(preview.follow_up.follow_up_date);
    if (
      preview.follow_up.follow_up_comment ||
      preview.follow_up.follow_up_service ||
      safeFollowUpDate ||
      isSkipped("followup")
    ) {
      payload.follow_up = {
        follow_up_comment: preview.follow_up.follow_up_comment,
        follow_up_service: preview.follow_up.follow_up_service,
        follow_up_date: safeFollowUpDate,
      };
    }
  }

  if (has("lab") && (preview.lab_tests.length || isSkipped("lab"))) {
    payload.lab_test_and_diagnostics = {
      lab_test_added: preview.lab_tests,
      lab_test_recommended: emrDetails?.lab_test_and_diagnostics?.lab_test_recommended ?? [],
    };
  }

  if (has("treatments") && (preview.treatments.length || preview.treatment_instructions || isSkipped("treatments"))) {
    payload.treatments = {
      treatments_added: preview.treatments,
      treatments_instructions: preview.treatment_instructions || "",
    };
  }

  if (has("medications") && (preview.medicines.length || isSkipped("medications"))) {
    payload.medicines = {
      medicines_added: preview.medicines,
      medicines_instructions: emrDetails?.medicines?.medicines_instructions || "",
    };
  }

  if (has("deworming") && (preview.deworming.length || isSkipped("deworming"))) {
    payload.deworming_meds_treatments = preview.deworming.map((item: any) => ({
      id: item?.id ?? null,
      name: item?.name ?? "",
      quantity: typeof item?.quantity === "number" && item.quantity > 0 ? item.quantity : 1,
      deworming_product_type: item?.deworming_product_type ?? "medicine",
      remark: item?.remark ?? "",
      item_tag: item?.item_tag ?? "#deworming",
      prescription_type: item?.prescription_type ?? undefined,
      is_child: item?.is_child ?? false,
      applied_item_group_id: item?.applied_item_group_id ?? undefined,
    })) as IEMRUpdatePayloadProps["deworming_meds_treatments"];
  }

  if (has("food") && (preview.food_added.length || preview.food_instructions || isSkipped("food"))) {
    payload.food_and_diet = {
      food_added: preview.food_added,
      food_instructions: preview.food_instructions || "",
    };
  }

  return payload;
};

const buildRestorePayload = (
  sent: IEMRUpdatePayloadProps,
  emrDetails: IEMRUpdatePayloadProps | null | undefined,
): IEMRUpdatePayloadProps => {
  const before = emrDetails ?? {};
  const restore: IEMRUpdatePayloadProps = {
    emr_status: before.emr_status,
    remove_children: before.remove_children ?? [],
  };

  for (const key of Object.keys(sent) as (keyof IEMRUpdatePayloadProps)[]) {
    switch (key) {
      case "emr_status":
      case "remove_children":
        break;
      case "subjective_history":
      case "physical_examinations":
      case "diagnosis":
      case "prognosis":
      case "medical_instructions":
        restore[key] = before[key] ?? "";
        break;
      case "tentative_diagnosis":
      case "definitive_diagnosis":
        restore[key] = before[key] ?? [];
        break;
      case "follow_up":
        restore.follow_up = before.follow_up ?? {
          follow_up_comment: "",
          follow_up_service: "",
          follow_up_date: null,
        };
        break;
      case "lab_test_and_diagnostics":
        restore.lab_test_and_diagnostics = before.lab_test_and_diagnostics ?? {
          lab_test_added: [],
          lab_test_recommended: [],
        };
        break;
      case "treatments":
        restore.treatments = before.treatments ?? {
          treatments_added: [],
          treatments_instructions: "",
        };
        break;
      case "medicines":
        restore.medicines = before.medicines ?? { medicines_added: [], medicines_instructions: "" };
        break;
      case "deworming_meds_treatments":
        restore.deworming_meds_treatments = before.deworming_meds_treatments ?? [];
        break;
      case "food_and_diet":
        restore.food_and_diet = before.food_and_diet ?? { food_added: [], food_instructions: "" };
        break;
      default:
        break;
    }
  }

  return restore;
};

const isBlankValue = (v: unknown): boolean => {
  if (v === null || v === undefined) return true;
  if (typeof v === "string") return !v.trim() || v.trim().toLowerCase() === "none";
  if (typeof v === "number") return v === 0;
  return false;
};

const isEmptyList = (v: unknown): boolean => !Array.isArray(v) || v.length === 0;

const pendingEmrGaps = (
  emrDetails: IEMRUpdatePayloadProps | null | undefined,
): { group: string; fields: string[] }[] => {
  const d = emrDetails ?? {};
  const checkup = d.basic_checkup ?? {};

  const groups = [
    {
      group: "Clinical",
      checks: [
        { label: "Chief Complaint & History", empty: isBlankValue(d.subjective_history) },
        { label: "Physical Examination", empty: isBlankValue(d.physical_examinations) },
        {
          label: "Diagnosis",
          empty:
            isBlankValue(d.diagnosis) &&
            isEmptyList(d.tentative_diagnosis) &&
            isEmptyList(d.definitive_diagnosis),
        },
        { label: "Prognosis", empty: isBlankValue(d.prognosis) },
        { label: "Medical Instructions", empty: isBlankValue(d.medical_instructions) },
        {
          label: "Follow-up",
          empty:
            isBlankValue(d.follow_up?.follow_up_comment) &&
            isBlankValue(d.follow_up?.follow_up_service) &&
            isBlankValue(d.follow_up?.follow_up_date),
        },
      ],
    },
    {
      group: "Billable",
      checks: [
        { label: "Medications", empty: isEmptyList(d.medicines?.medicines_added) },
        { label: "Lab & Diagnostics", empty: isEmptyList(d.lab_test_and_diagnostics?.lab_test_added) },
        {
          label: "Treatments",
          empty:
            isEmptyList(d.treatments?.treatments_added) &&
            isBlankValue(d.treatments?.treatments_instructions),
        },
        { label: "Deworming", empty: isEmptyList(d.deworming_meds_treatments) },
        {
          label: "Food & Diet",
          empty:
            isEmptyList(d.food_and_diet?.food_added) &&
            isBlankValue(d.food_and_diet?.food_instructions),
        },
      ],
    },
    {
      group: "Not captured by voice",
      checks: [
        { label: "Deworming date", empty: isBlankValue(checkup?.deworming?.new_date) },
        { label: "Vaccination status", empty: isBlankValue(checkup?.vaccination?.status) },
        { label: "Body Condition Score", empty: isBlankValue(d.body_condition_score) },
        { label: "Dental Score", empty: isBlankValue(d.dental_score) },
      ],
    },
  ];

  return groups
    .map(({ group, checks }) => ({
      group,
      fields: checks.filter((c) => c.empty).map((c) => c.label),
    }))
    .filter((g) => g.fields.length > 0);
};

const countFilledSections = (preview: VoicePreviewState): number =>
  [
    !!preview.subjective_history,
    !!preview.physical_examinations,
    !!(preview.diagnosis || preview.prognosis || preview.tentative_diagnosis.length || preview.definitive_diagnosis.length),
    !!preview.medical_instructions,
    !!(preview.follow_up.follow_up_comment || preview.follow_up.follow_up_service || preview.follow_up.follow_up_date),
    preview.medicines.length > 0,
    preview.lab_tests.length > 0,
    preview.treatments.length > 0 || !!preview.treatment_instructions,
    preview.deworming.length > 0,
    preview.food_added.length > 0 || !!preview.food_instructions,
  ].filter(Boolean).length;

// One per card that has anything in it — a card with 5 medicines still counts once.
const countFilledClinicalSections = (preview: VoicePreviewState): number =>
  [
    !!preview.subjective_history,
    !!preview.physical_examinations,
    !!(preview.diagnosis || preview.prognosis || preview.tentative_diagnosis.length || preview.definitive_diagnosis.length),
    !!preview.medical_instructions,
    !!(preview.follow_up.follow_up_comment || preview.follow_up.follow_up_service || preview.follow_up.follow_up_date),
  ].filter(Boolean).length;

const countFilledOrderSections = (preview: VoicePreviewState): number =>
  [
    preview.medicines.length > 0,
    preview.lab_tests.length > 0,
    preview.treatments.length > 0 || !!preview.treatment_instructions,
    preview.deworming.length > 0,
    preview.food_added.length > 0 || !!preview.food_instructions,
  ].filter(Boolean).length;

const estimateMinutesSaved = (preview: VoicePreviewState): number => {
  const text = [
    preview.subjective_history,
    preview.physical_examinations,
    preview.diagnosis,
    preview.prognosis,
    preview.medical_instructions,
    preview.treatment_instructions,
    preview.food_instructions,
    preview.follow_up.follow_up_comment,
    ...preview.tentative_diagnosis,
    ...preview.definitive_diagnosis,
  ].join(" ").length;
  const rows =
    preview.medicines.length +
    preview.lab_tests.length +
    preview.treatments.length +
    preview.deworming.length +
    preview.food_added.length;
  return Math.max(1, Math.round((text + rows * 60) / 200));
};

const VoiceEMRRecorder = ({
  emrId,
  emrStatus: _emrStatus,
  emrDetails,
  onSuccess,
  isOpen,
  onClose,
  onRestore,
  autoStart = false,
  autoResume = false,
  fullHeight = false,
  onAutoStartConsumed,
  onDurationChange,
}: VoiceEMRRecorderProps) => {
  const configuration = useConfiguration();
  const stateAccessToken = useAppSelector((state) => state?.auth?.authorization?.accessToken);
  const userLogInfo = useAppSelector((state) => state?.auth?.user?.email);
  const userId = useAppSelector((state) => state?.auth?.user?.user_id);
  const mappedClinicId = useAppSelector((state) => state?.root?.UIStates?.mappedClinicId);

  const { execute: updateEMR } = useUpdateTempEMRAPI();
  const { execute: clickLogAPI } = useClickLogAPI();
  const { execute: fetchVoiceSession } = useVoiceEMRSessionAPI();
  const { generateText, generateItems } = useVoiceEMRGenerateAPI();

  const logVoiceEMRClick = useCallback(
    (event_type: string, cta_text: string, extra: Partial<IClickLogBody> = {}) => {
      const body: IClickLogBody = {
        event_type,
        cta_text,
        scr_id: "VOICE_TO_EMR",
        timestamp: Date.now(),
        user_id: userId,
        vms_user_id: userLogInfo,
        mobile: emrDetails?.customer_details?.mobile ?? emrDetails?.customer_details?.mobile_number,
        emr_id: emrId ?? undefined,
        client_id: emrDetails?.customer_details?.customer_id,
        pet_id: emrDetails?.pet_details?.pet_id,
        clinic_id: mappedClinicId,
        ...extra,
      };
      clickLogAPI(body);
    },
    [clickLogAPI, userId, userLogInfo, emrDetails, emrId, mappedClinicId],
  );

  // Fire "start recording" only once per EMR (per open session). Re-records,
  // retries and reconnects on the same EMR must not re-count it. In-memory only,
  // so a page reload legitimately starts a fresh count.
  const startLoggedEmrsRef = useRef<Set<string>>(new Set());

  const [phase, setPhase] = useState<Phase>("idle");
  const [isPaused, setIsPaused] = useState(false);
  const [duration, setDuration] = useState(0);
  useEffect(() => {
    onDurationChange?.(duration);
  }, [duration, onDurationChange]);
  const [transcript, setTranscript] = useState("");
  const [displayedTranscript, setDisplayedTranscript] = useState("");
  // The live transcript is collapsed by default — the doctor is talking to the
  // pet parent, not reading along. Tapping the "Voice to EMR" title reveals it.
  const [isTranscriptVisible, setIsTranscriptVisible] = useState(false);
  const [statusText, setStatusText] = useState("Tap the mic to start recording.");
  const [isApplying, setIsApplying] = useState(false);
  const [preview, setPreview] = useState<VoicePreviewState | null>(null);
  const [hasUserEdited, setHasUserEdited] = useState(false);
  const hasUserEditedRef = useRef(false);
  useEffect(() => { hasUserEditedRef.current = hasUserEdited; }, [hasUserEdited]);
  const previewRef = useRef<VoicePreviewState | null>(null);
  useEffect(() => { previewRef.current = preview; }, [preview]);
  const mergeIntoPreviewRef = useRef(false);
  const [socketUnstable, setSocketUnstable] = useState(false);
  const [socketEmrId, setSocketEmrId] = useState<string | null>(null);
  const [textResultReceived, setTextResultReceived] = useState(false);
  const [itemsResultReceived, setItemsResultReceived] = useState(false);
  const [sessionPrompt, setSessionPrompt] = useState<IVoiceEMRSessionState | null>(null);
  const [checkingSession, setCheckingSession] = useState(false);
  const [pendingBytes, setPendingBytes] = useState(0);
  const [queueFull, setQueueFull] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [lastFault, setLastFault] = useState<string | null>(null);
  const [connectAttempts, setConnectAttempts] = useState(0);
  const [sessionKey, setSessionKey] = useState(0);
  const [applyingSection, setApplyingSection] = useState<SectionKey | null>(null);
  const [appliedSections, setAppliedSections] = useState<Set<SectionKey>>(new Set());
  const [skippedSections, setSkippedSections] = useState<Set<SectionKey>>(new Set());
  const [activeTab, setActiveTab] = useState<PreviewTab>("clinical");

  const previewScrollRef = useRef<HTMLDivElement | null>(null);
  const clinicalPanelRef = useRef<HTMLDivElement | null>(null);
  const ordersPanelRef = useRef<HTMLDivElement | null>(null);
  const spyPausedRef = useRef(false);
  const spyResumeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => {
    if (spyResumeTimerRef.current) clearTimeout(spyResumeTimerRef.current);
  }, []);

  const resetPreviewScroll = useCallback(() => {
    setActiveTab("clinical");
    spyPausedRef.current = false;
    if (spyResumeTimerRef.current) clearTimeout(spyResumeTimerRef.current);
    previewScrollRef.current?.scrollTo({ top: 0 });
  }, []);

  const [appliedSummary, setAppliedSummary] = useState<{
    sections: number;
    seconds: number;
    minutesSaved: number;
    restore: IEMRUpdatePayloadProps;
  } | null>(null);
  const [isUndoing, setIsUndoing] = useState(false);
  const emrGaps = useMemo(() => pendingEmrGaps(emrDetails), [emrDetails]);

  const audioContextRef = useRef<AudioContext | null>(null);
  const workletNodeRef = useRef<AudioWorkletNode | null>(null);
  const sourceNodeRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const pendingEmrTextRef = useRef<IVoiceEMRText | null>(null);
  const pendingEmrItemsRef = useRef<IVoiceEMRItems | null>(null);

  const voiceIdRef = useRef<string | null>(null);

  const emrIdRef = useRef<string | null>(emrId);
  useEffect(() => {
    emrIdRef.current = emrId;
  }, [emrId]);

  const pendingBytesRef = useRef(0);
  const drainingRef = useRef(false);
  const readyStateRef = useRef<ReadyState>(ReadyState.UNINSTANTIATED);
  const pendingDoneRef = useRef(false);
  const fatalCloseRef = useRef(false);

  const transcriptScrollRef = useRef<HTMLDivElement | null>(null);

  const phaseRef = useRef<Phase>("idle");
  useEffect(() => {
    phaseRef.current = phase;
  }, [phase]);

  const doneAckedRef = useRef(false);

  const sessionCompleteRef = useRef(false);

  const stateAccessTokenRef = useRef(stateAccessToken);
  useEffect(() => {
    stateAccessTokenRef.current = stateAccessToken;
  }, [stateAccessToken]);

  const readAccessToken = useCallback(() => {
    const raw = String(
      localStorage.getItem(configuration.localStorage.ACCESS_TOKEN.key) || stateAccessTokenRef.current || "",
    );
    const jwt = raw.match(/[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/);
    return jwt ? jwt[0] : raw.replace(/^bearer\s+/i, "").trim();
  }, [configuration]);

  const accessToken = useMemo(() => {
    const local = localStorage.getItem(configuration.localStorage.ACCESS_TOKEN.key) || "";
    const raw = String(stateAccessToken || local || "");
    const jwt = raw.match(/[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/);
    return jwt ? jwt[0] : raw.replace(/^bearer\s+/i, "").trim();
  }, [stateAccessToken, configuration]);

  useEffect(() => {
    setSocketEmrId(null);
  }, [emrId]);

  const socketModeRef = useRef<"new" | "continue">("new");
  // Set when the mic could not be acquired for this session. The socket still
  // connects (session start does not depend on the mic), so status updates must
  // not claim we are listening.
  const micUnavailableRef = useRef(false);

  const canConnect = !!socketEmrId && !!accessToken;
  const getSocketUrl = useCallback(async () => {
    void sessionKey;
    const base = EMR_APIS.VOICE_EMR_WS.baseURL;
    const qs = new URLSearchParams({
      emr_id: socketEmrId ?? "",
      token: readAccessToken(),
      mode: socketModeRef.current,
    }).toString();
    return `${base}?${qs}`;
  }, [socketEmrId, readAccessToken, sessionKey]);

  const { sendMessage, sendJsonMessage, lastJsonMessage, readyState } = useWebSocket(
    canConnect ? getSocketUrl : null,
    {
      shouldReconnect: () =>
        !fatalCloseRef.current &&
        !sessionCompleteRef.current &&
        (phaseRef.current === "connecting" ||
          phaseRef.current === "recording" ||
          pendingDoneRef.current ||
          pendingBytesRef.current > 0),
      reconnectAttempts: 10,
      reconnectInterval: 1500,
      retryOnError: true,
      share: false,
      onOpen: () => {
        if (!micUnavailableRef.current) setLastFault(null);
        setConnectAttempts((n) => n + 1);
        if (phaseRef.current === "connecting") {
          setPhase("recording");
          setStatusText(micUnavailableRef.current ? MIC_UNAVAILABLE_STATUS : "Listening...");
        } else if (phaseRef.current === "recording") {
          setStatusText("Reconnected — resuming...");
        }
        else if (phaseRef.current === "idle") {
          // eslint-disable-next-line no-console
          console.warn("[voice-emr] socket opened while idle — closing it");
          setSocketEmrId(null);
          return;
        }
        if (!isPausedRef.current) startTimer();
      },
      onClose: (event) => {
        // eslint-disable-next-line no-console
        console.warn("[voice-emr] socket closed", {
          code: event.code,
          reason: event.reason,
          wasClean: event.wasClean,
          phase: phaseRef.current,
        });
        if (event.code === 1008) {
          fatalCloseRef.current = true;
          setStatusText(
            "Your login expired. Refresh the page, sign in, then reopen Voice to EMR and choose "
            + "\u201cContinue with existing recording\u201d \u2014 nothing recorded is lost.",
          );
        }
        if (!(sessionCompleteRef.current && (event.code === 1000 || event.code === 1001))) {
          setLastFault(
            `socket closed ${event.code}${event.reason ? ` (${event.reason})` : ""}${CLOSE_HINTS[event.code] ? ` — ${CLOSE_HINTS[event.code]}` : ""}`,
          );
        }
        if (phaseRef.current === "connecting") {
          if (fatalCloseRef.current) {
            fullCleanup();
            setPhase("idle");
          } else {
            setStatusText("Connecting is taking a moment — retrying...");
          }
        }
      },
      onError: () => {
        setLastFault((prev) => prev ?? "socket error (no detail available from the browser)");
      },
      onReconnectStop: (attempts) => {
        setLastFault(`gave up reconnecting after ${attempts} attempts`);
      },
    },
    canConnect,
  );

  const isSocketOpen = readyState === ReadyState.OPEN;
  const sendMessageRef = useRef(sendMessage);
  useEffect(() => {
    sendMessageRef.current = sendMessage;
  }, [sendMessage]);
  const sendJsonMessageRef = useRef(sendJsonMessage);
  useEffect(() => {
    sendJsonMessageRef.current = sendJsonMessage;
  }, [sendJsonMessage]);
  useEffect(() => {
    readyStateRef.current = readyState;
  }, [readyState]);

  const applyQueueStats = useCallback((bytes: number) => {
    pendingBytesRef.current = bytes;
    if (bytes < AUDIO_QUEUE_MAX_BYTES) setQueueFull(false);
  }, []);

  const drainQueue = useCallback(async () => {
    if (!emrId || drainingRef.current) return;
    if (readyStateRef.current !== ReadyState.OPEN) return;

    drainingRef.current = true;
    try {
      for (;;) {
        if (readyStateRef.current !== ReadyState.OPEN) break;
        const batch = await peekAudioChunks(emrId, 32);
        if (!batch.length) break;

        const sent: number[] = [];
        for (const chunk of batch) {
          if (readyStateRef.current !== ReadyState.OPEN) break;
          try {
            sendMessageRef.current(chunk.data, false);
            sent.push(chunk.id);
          } catch {
            break;
          }
        }
        if (!sent.length) break;
        await deleteAudioChunks(sent);
      }

      const stats = await getQueueStats(emrId);
      applyQueueStats(stats.bytes);

      if (!stats.count && pendingDoneRef.current && readyStateRef.current === ReadyState.OPEN) {
        pendingDoneRef.current = false;
        try {
          sendJsonMessageRef.current({ action: "done" });
        } catch {
          pendingDoneRef.current = true;
        }
      }
    } catch (e) {
      console.error("[voice-emr] drain failed", e);
    } finally {
      drainingRef.current = false;
    }
  }, [emrId, applyQueueStats]);

  const drainQueueRef = useRef(drainQueue);
  useEffect(() => {
    drainQueueRef.current = drainQueue;
  }, [drainQueue]);

  useEffect(() => {
    if (isSocketOpen) void drainQueue();
  }, [isSocketOpen, drainQueue]);

  useEffect(() => {
    if (phase === "idle") return;
    const sync = () => setPendingBytes(pendingBytesRef.current);
    sync();
    const timer = setInterval(sync, 1000);
    return () => clearInterval(timer);
  }, [phase]);

  useEffect(() => {
    if (phase !== "recording") {
      setSocketUnstable(false);
      return;
    }
    if (isSocketOpen) {
      setSocketUnstable(false);
      return;
    }
    const t = setTimeout(() => setSocketUnstable(true), 1500);
    return () => clearTimeout(t);
  }, [phase, isSocketOpen]);

  const startTimer = () => {
    if (timerRef.current) return;
    timerRef.current = setInterval(() => setDuration((d) => d + 1), 1000);
  };
  const stopTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const tearDownAudio = useCallback(() => {
    try {
      workletNodeRef.current?.port.close();
      workletNodeRef.current?.disconnect();
    } catch (error) {
      void error;
    }
    workletNodeRef.current = null;
    try {
      sourceNodeRef.current?.disconnect();
    } catch (error) {
      void error;
    }
    sourceNodeRef.current = null;
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (audioContextRef.current && audioContextRef.current.state !== "closed") {
      audioContextRef.current.close().catch(() => {});
    }
    audioContextRef.current = null;
  }, []);

  const isPausedRef = useRef(false);
  useEffect(() => {
    isPausedRef.current = isPaused;
  }, [isPaused]);

  const setupAudio = useCallback(async () => {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        channelCount: 1,
        echoCancellation: true,
        noiseSuppression: true,
      },
    });
    streamRef.current = stream;

    const AudioCtx = (window.AudioContext || (window as any).webkitAudioContext) as typeof AudioContext;
    const ctx = new AudioCtx();
    audioContextRef.current = ctx;

    await ctx.audioWorklet.addModule("/pcm-downsampler.js");

    const source = ctx.createMediaStreamSource(stream);
    sourceNodeRef.current = source;

    const node = new AudioWorkletNode(ctx, "pcm-downsampler", {
      numberOfInputs: 1,
      numberOfOutputs: 1,
      outputChannelCount: [1],
    });
    workletNodeRef.current = node;

    node.port.onmessage = (event) => {
      if (phaseRef.current !== "recording" || isPausedRef.current) return;
      const buf = event.data as ArrayBuffer;
      if (!emrIdRef.current) return;

      if (pendingBytesRef.current + buf.byteLength > AUDIO_QUEUE_MAX_BYTES) {
        setQueueFull(true);
        return;
      }

      pendingBytesRef.current += buf.byteLength;
      void enqueueAudioChunk(emrIdRef.current, buf)
        .then(() => drainQueueRef.current())
        .catch((e) => console.error("[voice-emr] enqueue failed", e));
    };

    source.connect(node);
    node.connect(ctx.destination);
  }, []);

  const fullCleanup = useCallback(() => {
    stopTimer();
    tearDownAudio();
    setIsPaused(false);
    setSocketEmrId(null);
  }, [tearDownAudio]);

  useEffect(() => {
    return () => {
      fullCleanup();
    };
  }, [fullCleanup]);

  useEffect(() => {
    if (phase !== "connecting" || isSocketOpen) return;
    const timeout = setTimeout(() => {
      if (phaseRef.current !== "connecting") return;
      // eslint-disable-next-line no-console
      console.warn("[voice-emr] connect timed out", { emrId, socketEmrId });
      const timedOut = `connect timed out after ${CONNECT_TIMEOUT_MS / 1000}s`;
      setLastFault((prev) => (prev ? `${prev} · ${timedOut}` : `${timedOut} — no response from the voice server`));
      fullCleanup();
      setPhase("idle");
      setStatusText("Couldn't connect — check your network, then tap the mic to try again.");
    }, CONNECT_TIMEOUT_MS);
    return () => clearTimeout(timeout);
  }, [phase, isSocketOpen, fullCleanup, emrId, socketEmrId]);

  const runGeneration = useCallback(
    async (voiceId: string) => {
      setPhase("processing");
      setStatusText("Generating EMR...");

      const applyText = (emrText: IVoiceEMRText | null) => {
        if (!emrText) return;
        pendingEmrTextRef.current = emrText;
        setTextResultReceived(true);
        setPreview((prev) => {
          const parsed = parsePreview(emrText, pendingEmrItemsRef.current);
          if (prev && mergeIntoPreviewRef.current) return mergePreview(prev, parsed);
          if (prev && hasUserEditedRef.current) return prev;
          if (!prev) return parsed;
          return {
            ...prev,
            subjective_history: parsed.subjective_history,
            physical_examinations: parsed.physical_examinations,
            diagnosis: parsed.diagnosis,
            prognosis: parsed.prognosis,
            tentative_diagnosis: parsed.tentative_diagnosis,
            definitive_diagnosis: parsed.definitive_diagnosis,
            medical_instructions: parsed.medical_instructions,
            follow_up: parsed.follow_up,
          };
        });
      };

      const applyItems = (emrItems: IVoiceEMRItems | null) => {
        if (!emrItems) return;
        pendingEmrItemsRef.current = emrItems;
        setItemsResultReceived(true);
        setPreview((prev) => {
          const parsed = parsePreview(pendingEmrTextRef.current, emrItems);
          if (prev && mergeIntoPreviewRef.current) return mergePreview(prev, parsed);
          if (prev && hasUserEditedRef.current) return prev;
          return parsed;
        });
      };

      const [textResult, itemsResult] = await Promise.all([
        generateText(voiceId).then((r) => {
          applyText(r);
          return r;
        }),
        generateItems(voiceId).then((r) => {
          applyItems(r);
          return r;
        }),
      ]);

      if (!textResult && !itemsResult) {
        setLastFault("both generate calls failed");
        setStatusText("Generation failed. Tap retry to try again.");
        return;
      }
      if (!textResult || !itemsResult) {
        setLastFault(`${textResult ? "items" : "text"} half failed to generate`);
      }
      setPhase("ready");
      setStatusText("Review the parsed EMR below, then Apply.");
    },
    [generateText, generateItems],
  );

  const retryGeneration = () => {
    const voiceId = voiceIdRef.current;
    if (!voiceId) return;
    logVoiceEMRClick("VOICE_EMR_RETRY_GENERATE_CLICKED", "Retry generate");
    void runGeneration(voiceId);
  };

  useEffect(() => {
    if (!lastJsonMessage) return;
    const msg = lastJsonMessage as any;
    const type = msg?.type;

    if (type === "session") {
      voiceIdRef.current = String(msg?.voice_id ?? "") || null;
      socketModeRef.current = "continue";
      const prior = String(msg?.transcript_so_far ?? "");
      if (prior) {
        setTranscript(prior);
        setDisplayedTranscript(prior);
      }
      if (phaseRef.current !== "processing") {
        setStatusText(micUnavailableRef.current ? MIC_UNAVAILABLE_STATUS : "Listening...");
      }
      return;
    }

    if (type === "transcript") {
      const incoming = String(msg?.text ?? "");
      if (incoming) {
        setTranscript((prev) => (prev ? `${prev} ${incoming}` : incoming));
      }
      if (phaseRef.current !== "processing") setStatusText("Listening...");
      return;
    }

    if (type === "done") {
      doneAckedRef.current = true;
      sessionCompleteRef.current = true;
      voiceIdRef.current = String(msg?.voice_id ?? "") || voiceIdRef.current;
      tearDownAudio();
      stopTimer();
      setIsPaused(false);
      if (voiceIdRef.current) {
        void runGeneration(voiceIdRef.current);
      } else {
        setLastFault("server acknowledged the recording without a voice_id");
        setPhase("idle");
        setStatusText("Tap the mic to start recording.");
      }
      return;
    }

    if (type === "error") {
      setLastFault(`server: ${msg?.message || "unspecified error"}`);
      tearDownAudio();
      stopTimer();
      pendingEmrTextRef.current = null;
      pendingEmrItemsRef.current = null;
      doneAckedRef.current = false;
      sessionCompleteRef.current = false;
      setApplyingSection(null);
      setIsPaused(false);

      if (previewRef.current) {
        setPhase("ready");
        setStatusText(
          "That stretch added nothing — your earlier result is still here. Apply it, or record again.",
        );
      } else {
        voiceIdRef.current = null;
        socketModeRef.current = "new";
        mergeIntoPreviewRef.current = false;
        setTextResultReceived(false);
        setItemsResultReceived(false);
        resetPreviewScroll();
        setAppliedSummary(null);
        setPreview(null);
        setHasUserEdited(false);
        setAppliedSections(new Set());
        setSkippedSections(new Set());
        setSocketEmrId(null);
        setTranscript("");
        setDisplayedTranscript("");
        setDuration(0);
        setPhase("idle");
        setStatusText(
          msg?.message ? String(msg.message) : "Nothing could be generated. Tap the mic to record again.",
        );
      }
    }
  }, [lastJsonMessage, tearDownAudio, runGeneration]);

  useEffect(() => {
    if (displayedTranscript === transcript) return;
    if (displayedTranscript.length > transcript.length) {
      setDisplayedTranscript(transcript);
      return;
    }
    const timer = setTimeout(() => {
      const nextLen = Math.min(displayedTranscript.length + 1, transcript.length);
      setDisplayedTranscript(transcript.slice(0, nextLen));
    }, TYPEWRITER_INTERVAL_MS);
    return () => clearTimeout(timer);
  }, [transcript, displayedTranscript]);

  useEffect(() => {
    const el = transcriptScrollRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distanceFromBottom < 40) {
      el.scrollTop = el.scrollHeight;
    }
  }, [displayedTranscript]);

  const beginSession = async (mode: "new" | "continue") => {
    pendingDoneRef.current = false;
    fatalCloseRef.current = false;
    micUnavailableRef.current = false;
    setQueueFull(false);
    if (mode === "new" && emrId) {
      await clearAudioQueue(emrId).catch(() => {});
      pendingBytesRef.current = 0;
      setPendingBytes(0);
    } else if (emrId) {
      const stats = await getQueueStats(emrId).catch(() => ({ count: 0, bytes: 0 }));
      pendingBytesRef.current = stats.bytes;
      setPendingBytes(stats.bytes);
    }
    pendingEmrTextRef.current = null;
    pendingEmrItemsRef.current = null;
    voiceIdRef.current = null;
    doneAckedRef.current = false;
    sessionCompleteRef.current = false;
    socketModeRef.current = mode;
    let carriedPreview: VoicePreviewState | null = null;
    if (mode === "continue") {
      const fromEmr = previewFromEmrDetails(emrDetails);
      const onScreen = previewRef.current;
      const hasSavedEmr = countFilledSections(fromEmr) > 0;
      if (hasSavedEmr && onScreen) carriedPreview = mergePreview(fromEmr, onScreen);
      else if (hasSavedEmr) carriedPreview = fromEmr;
      else carriedPreview = onScreen;
    }
    mergeIntoPreviewRef.current = !!carriedPreview;

    resetPreviewScroll();
    setAppliedSummary(null);
    setTranscript("");
    setDisplayedTranscript("");
    setApplyingSection(null);
    setAppliedSections(new Set());
    setSkippedSections(new Set());
    if (carriedPreview) {
      setPreview(carriedPreview);
      previewRef.current = carriedPreview;
      setTextResultReceived(true);
      setItemsResultReceived(true);
    } else {
      setTextResultReceived(false);
      setItemsResultReceived(false);
      setPreview(null);
      setHasUserEdited(false);
    }
    if (mode === "new") setDuration(0);
    setSessionKey((key) => key + 1);
    setSocketEmrId(emrId);
    setPhase("connecting");
    setStatusText("Connecting...");

    try {
      await setupAudio();
    } catch (e) {
      console.error(e);
      const err = e instanceof Error ? e : null;
      const name = err?.name ?? "";
      const detail = err ? `${err.name}: ${err.message}` : String(e);
      // NotAllowedError/SecurityError = the doctor blocked or dismissed the mic
      // prompt; anything else (NotFoundError etc.) is a device/hardware problem.
      const permissionDenied =
        name === "NotAllowedError" || name === "SecurityError" || /permission/i.test(detail);
      logVoiceEMRClick(
        permissionDenied ? "VOICE_EMR_MIC_PERMISSION_DENIED" : "VOICE_EMR_MIC_UNAVAILABLE",
        permissionDenied ? "Microphone permission denied" : "Microphone unavailable",
        { source: detail },
      );
      setLastFault(`microphone unavailable — ${detail}`);
      // Release whatever part of the audio graph was built, but keep the socket:
      // the session start is sent regardless of the mic (product decision), so
      // the server-side session exists even when the mic is blocked or missing.
      micUnavailableRef.current = true;
      tearDownAudio();
      setStatusText(MIC_UNAVAILABLE_STATUS);
      // No popup on a blocked mic (product decision) — status text only, with
      // the reason carried on the event above.
    }
  };

  const startRecording = async () => {
    if (phaseRef.current === "connecting" || phaseRef.current === "recording") return;
    if (!emrId) {
      setLastFault("EMR not loaded yet");
      return setStatusText("EMR is still loading. Try again in a moment.");
    }
    if (!accessToken) {
      setLastFault("no auth token in this session");
      return setStatusText("You appear to be signed out. Refresh and sign in again.");
    }
    // Once per EMR: a doctor who taps the mic again, re-records, or reconnects on
    // the same EMR must not re-count the start.
    if (!startLoggedEmrsRef.current.has(emrId)) {
      startLoggedEmrsRef.current.add(emrId);
      logVoiceEMRClick("VOICE_EMR_START_RECORDING_CLICKED", "Start recording");
    }

    setCheckingSession(true);
    const existing = await fetchVoiceSession(emrId);
    setCheckingSession(false);

    if (existing) {
      // Carried over from another page — pick up where that recording left off
      // rather than making the doctor confirm a prompt they did not ask for.
      if (autoResume) {
        const prior = existing.transcript ?? "";
        const priorSeconds = Math.round(existing.audio_duration_seconds ?? 0);
        await beginSession("continue");
        if (priorSeconds > 0) setDuration(priorSeconds);
        if (prior) {
          setTranscript(prior);
          setDisplayedTranscript(prior);
        }
        return;
      }
      setSessionPrompt(existing);
      return;
    }
    await beginSession("new");
  };

  const promptStartNew = async () => {
    logVoiceEMRClick("VOICE_EMR_PROMPT_NEW_CLICKED", "New recording");
    setSessionPrompt(null);
    await beginSession("new");
  };

  const continueRecording = async () => {
    if (!emrId) return;
    logVoiceEMRClick("VOICE_EMR_CONTINUE_AFTER_STOP_CLICKED", "Continue recording");
    const prior = transcript;
    await beginSession("continue");
    if (prior) {
      setTranscript(prior);
      setDisplayedTranscript(prior);
    }
  };

  const promptContinue = async () => {
    logVoiceEMRClick("VOICE_EMR_PROMPT_CONTINUE_CLICKED", "Continue existing recording");
    const prior = sessionPrompt?.transcript ?? "";
    const priorSeconds = Math.round(sessionPrompt?.audio_duration_seconds ?? 0);
    setSessionPrompt(null);
    await beginSession("continue");
    if (priorSeconds > 0) setDuration(priorSeconds);
    if (prior) {
      setTranscript(prior);
      setDisplayedTranscript(prior);
    }
  };

  const promptShowPrevious = () => {
    logVoiceEMRClick("VOICE_EMR_PROMPT_PREVIOUS_CLICKED", "Get previous results");
    const session = sessionPrompt;
    setSessionPrompt(null);
    if (!session?.emr_data) {
      setLastFault("completed session had no stored emr_data");
      setStatusText("Previous results are unavailable. Record again.");
      return;
    }
    voiceIdRef.current = session.voice_id;
    mergeIntoPreviewRef.current = false;
    pendingEmrTextRef.current = session.emr_data as IVoiceEMRText;
    pendingEmrItemsRef.current = session.emr_data as IVoiceEMRItems;
    setTranscript(session.transcript ?? "");
    setDisplayedTranscript(session.transcript ?? "");
    setPreview(parsePreview(session.emr_data as IVoiceEMRText, session.emr_data as IVoiceEMRItems));
    setTextResultReceived(true);
    setItemsResultReceived(true);
    setDuration(Math.round(session.audio_duration_seconds ?? 0));
    setPhase("ready");
    setStatusText("Showing the previous result. Review, then Apply.");
  };

  const autoStartFiredRef = useRef(false);
  useEffect(() => {
    if (!autoStart || !isOpen || autoStartFiredRef.current) return;
    if (!emrId || !accessToken) return;
    autoStartFiredRef.current = true;
    onAutoStartConsumed?.();
    if (phase !== "idle") return;
    void startRecording();
  }, [autoStart, isOpen, emrId, accessToken, phase]);

  useEffect(() => {
    if (!isOpen) autoStartFiredRef.current = false;
  }, [isOpen]);

  const dismissPrompt = () => {
    logVoiceEMRClick("VOICE_EMR_PROMPT_DISMISSED", "Dismiss recording prompt");
    setSessionPrompt(null);
  };

  const togglePause = () => {
    if (phase !== "recording") return;
    if (isPaused) {
      logVoiceEMRClick("VOICE_EMR_RESUME_RECORDING_CLICKED", "Resume");
      audioContextRef.current?.resume().catch(() => {});
      startTimer();
      setIsPaused(false);
      setStatusText("Listening...");
    } else {
      logVoiceEMRClick("VOICE_EMR_PAUSE_RECORDING_CLICKED", "Pause");
      audioContextRef.current?.suspend().catch(() => {});
      stopTimer();
      setIsPaused(true);
      setStatusText("Paused");
    }
  };

  const stopRecording = () => {
    if (phase !== "recording" && phase !== "connecting") return;
    logVoiceEMRClick("VOICE_EMR_STOP_RECORDING_CLICKED", "Stop recording");
    stopTimer();
    tearDownAudio();
    setIsPaused(false);
    doneAckedRef.current = false;
    setPhase("processing");
    pendingDoneRef.current = true;
    setStatusText(
      pendingBytesRef.current > 0 ? "Uploading remaining audio..." : "Finishing up...",
    );
    void drainQueue();
  };

  const requestDiscard = () => {
    logVoiceEMRClick("VOICE_EMR_RERECORD_CLICKED", "Re-record");
    setConfirmDiscard(true);
  };

  const confirmDiscardAndRestart = async () => {
    setConfirmDiscard(false);
    logVoiceEMRClick("VOICE_EMR_RERECORD_CONFIRMED", "Confirm re-record");

    sessionCompleteRef.current = true;
    pendingDoneRef.current = false;
    fullCleanup();
    stopTimer();

    voiceIdRef.current = null;
    pendingEmrTextRef.current = null;
    pendingEmrItemsRef.current = null;
    doneAckedRef.current = false;
    mergeIntoPreviewRef.current = false;
    setTextResultReceived(false);
    setItemsResultReceived(false);
    resetPreviewScroll();
    setAppliedSummary(null);
    setPreview(null);
    setHasUserEdited(false);
    setApplyingSection(null);
    setAppliedSections(new Set());
    setSkippedSections(new Set());
    setPhase("idle");

    sessionCompleteRef.current = false;
    await beginSession("new");
  };

  const applyPreview = async () => {
    if (!emrId || !preview) return;
    logVoiceEMRClick("VOICE_EMR_APPLY_ALL_CLICKED", "Apply to EMR");
    setIsApplying(true);
    const payload = buildUpdatePayload(preview, emrDetails, new Set(), skippedSections);
    const restore = buildRestorePayload(payload, emrDetails);
    const result = await updateEMR(emrId, payload);
    setIsApplying(false);
    if (result?.status) {
      toast.success("EMR auto-filled from voice");
      onSuccess(result.message);
      setAppliedSummary({
        sections: countFilledSections(preview),
        seconds: duration,
        minutesSaved: estimateMinutesSaved(preview),
        restore,
      });
    }
  };

  const undoApply = async () => {
    if (!emrId || !appliedSummary) return;
    logVoiceEMRClick("VOICE_EMR_UNDO_APPLY_CLICKED", "Undo & re-edit");
    setIsUndoing(true);
    const result = await updateEMR(emrId, appliedSummary.restore);
    setIsUndoing(false);
    if (result?.status) {
      onSuccess(result.message);
      setAppliedSummary(null);
      setAppliedSections(new Set());
    setSkippedSections(new Set());
      toast.success("Undone — edit and apply again");
    }
  };

  const applySection = async (key: SectionKey) => {
    if (!emrId || !preview) return;
    logVoiceEMRClick("VOICE_EMR_APPLY_SECTION_CLICKED", `Apply ${key} section`, {
      emr_search_type: key,
    });
    setApplyingSection(key);
    const payload = buildUpdatePayload(preview, emrDetails, new Set([key]), skippedSections);
    const result = await updateEMR(emrId, payload);
    setApplyingSection(null);
    if (result?.status) {
      setAppliedSections((prev) => {
        const next = new Set(prev);
        next.add(key);
        return next;
      });
      onSuccess(result.message);
      toast.success("Section applied to EMR");
      setTimeout(() => scrollToEmrSection(key), 100);
    }
  };

  const updatePreview = <K extends keyof VoicePreviewState>(key: K, value: VoicePreviewState[K]) => {
    setHasUserEdited(true);
    setPreview((prev) => (prev ? { ...prev, [key]: value } : prev));
  };

  const updateFollowUp = <K extends keyof VoicePreviewState["follow_up"]>(
    key: K,
    value: VoicePreviewState["follow_up"][K],
  ) => {
    setHasUserEdited(true);
    setPreview((prev) => (prev ? { ...prev, follow_up: { ...prev.follow_up, [key]: value } } : prev));
  };

  const clearSection = (fields: Partial<VoicePreviewState>) => {
    logVoiceEMRClick("VOICE_EMR_CLEAR_SECTION_CLICKED", "Clear section", {
      emr_search_type: Object.keys(fields).join(","),
    });
    setHasUserEdited(true);
    setPreview((prev) => (prev ? { ...prev, ...fields } : prev));
  };

  const skipSection = (key: SectionKey, fields: Partial<VoicePreviewState>) => {
    logVoiceEMRClick("VOICE_EMR_SKIP_SECTION_CLICKED", `Skip ${key} section`, { emr_search_type: key });
    setSkippedSections((prev) => {
      const next = new Set(prev);
      next.add(key);
      return next;
    });
    // Back to a blank grey card — drop the applied state so the green border
    // and "Applied" badge clear along with the data.
    setAppliedSections((prev) => {
      if (!prev.has(key)) return prev;
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
    clearSection(fields);
  };

  const [isDismissed, setIsDismissed] = useState(false);
  const [isMinimised, setIsMinimised] = useState(false);
  useEffect(() => {
    if (isOpen) {
      setIsDismissed(false);
      setIsMinimised(false);
    }
  }, [isOpen]);

  const minimisePanel = () => {
    logVoiceEMRClick("VOICE_EMR_PANEL_MINIMISED", "Minimise voice panel");
    setIsMinimised(true);
    onClose();
  };

  // ── Drag-to-move (mirrors UIContainer/StickyPanel) ──────────────────────
  const panelRef = useRef<HTMLElement | null>(null);
  const miniRef = useRef<HTMLButtonElement | null>(null);
  const dragState = useRef({ isDragging: false, offsetX: 0, offsetY: 0 });
  const movedRef = useRef(false);
  const [isDragging, setIsDragging] = useState(false);
  // Until it is dragged, the panel stays pinned to the bottom-right so it grows
  // upward as content arrives instead of pushing off the bottom of the screen.
  const [hasDragged, setHasDragged] = useState(false);
  const [position, setPosition] = useState(() => getDockedPosition(false));

  // Re-dock whenever the panel flips between open and minimised.
  useEffect(() => {
    setPosition(getDockedPosition(!isOpen));
    setHasDragged(false);
  }, [isOpen]);

  useEffect(() => {
    if (!isDragging) return;

    const handlePointerMove = (event: PointerEvent) => {
      if (!dragState.current.isDragging) return;
      const container = isOpen ? panelRef.current : miniRef.current;
      if (!container) return;
      movedRef.current = true;
      const rect = container.getBoundingClientRect();
      const maxX = Math.max(8, window.innerWidth - rect.width - 8);
      const maxY = Math.max(8, window.innerHeight - HEADER_KEEP_VISIBLE);
      setPosition({
        x: clampValue(event.clientX - dragState.current.offsetX, 8, maxX),
        y: clampValue(event.clientY - dragState.current.offsetY, 8, maxY),
      });
    };

    const handlePointerUp = () => {
      if (!dragState.current.isDragging) return;
      dragState.current.isDragging = false;
      setIsDragging(false);
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
    window.addEventListener("pointercancel", handlePointerUp);
    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
      window.removeEventListener("pointercancel", handlePointerUp);
    };
  }, [isDragging, isOpen]);

  const startDrag = (event: ReactPointerEvent<HTMLElement>) => {
    if (event.button !== 0) return;
    const container = isOpen ? panelRef.current : miniRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    movedRef.current = false;
    setHasDragged(true);
    dragState.current = {
      isDragging: true,
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top,
    };
    setIsDragging(true);
  };

  const closePanel = () => {
    logVoiceEMRClick("VOICE_EMR_PANEL_CLOSED", "Close voice panel");
    setIsMinimised(false);
    setIsDismissed(true);
    onClose();
  };

  // Background pill → reopen the panel.
  const handleRestore = () => {
    logVoiceEMRClick("VOICE_EMR_RESTORE_CLICKED", "Restore voice panel");
    onRestore?.();
  };

  // "Keep recording" in the discard dialog — the doctor backed out of re-record.
  const cancelDiscard = () => {
    logVoiceEMRClick("VOICE_EMR_RERECORD_CANCELLED", "Keep recording");
    setConfirmDiscard(false);
  };

  const isRecording = phase === "recording";
  const isConnecting = phase === "connecting";
  const isProcessing = phase === "processing";
  const generationFailed = isProcessing && statusText.startsWith("Generation failed");
  const inPreviewMode = phase === "ready" || (isProcessing && !!preview && !generationFailed);
  const showProcessingLoader = isProcessing && (!preview || generationFailed);
  const recordDisabled = isProcessing || isApplying || checkingSession || !!sessionPrompt || !emrId || !accessToken;
  const hasStoppedRecording = !isRecording && !isConnecting && duration > 0;

  const filledSectionCount = preview ? countFilledSections(preview) : 0;

  const orderCount = preview ? countFilledOrderSections(preview) : 0;
  const clinicalCount = preview ? countFilledClinicalSections(preview) : 0;

  const handlePreviewScroll = useCallback(() => {
    const el = previewScrollRef.current;
    const orders = ordersPanelRef.current;
    if (!el || !orders || spyPausedRef.current || !inPreviewMode || !preview || appliedSummary) return;

    const offset = orders.getBoundingClientRect().top - el.getBoundingClientRect().top;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight <= SPY_BOTTOM_PX;
    setActiveTab(offset <= SPY_LINE_PX || atBottom ? "orders" : "clinical");
  }, [inPreviewMode, preview, appliedSummary]);

  const selectPreviewTab = useCallback((next: PreviewTab) => {
    setActiveTab(next);
    const el = previewScrollRef.current;
    const target = next === "orders" ? ordersPanelRef.current : clinicalPanelRef.current;
    if (!el || !target) return;

    spyPausedRef.current = true;
    if (spyResumeTimerRef.current) clearTimeout(spyResumeTimerRef.current);
    spyResumeTimerRef.current = setTimeout(() => {
      spyPausedRef.current = false;
    }, SPY_RESUME_MS);

    const top = el.scrollTop + (target.getBoundingClientRect().top - el.getBoundingClientRect().top);
    el.scrollTo({ top: Math.max(0, top - SPY_CLICK_GAP_PX), behavior: "smooth" });
  }, []);

  const connectionLabel = (() => {
    if (phase === "idle") return "Idle";
    if (phase === "connecting") return "Connecting...";
    if (phase === "recording") return socketUnstable ? "Reconnecting..." : "Connected";
    if (phase === "processing") return "Processing";
    if (phase === "ready") return "Ready";
    return "Idle";
  })();

  const dotColor = (() => {
    if (phase === "recording" && !socketUnstable) return "bg-green-500";
    if (phase === "connecting" || (phase === "recording" && socketUnstable))
      return "bg-yellow-500 animate-pulse";
    if (phase === "processing") return "bg-blue-500 animate-pulse";
    if (phase === "ready") return "bg-green-500";
    return "bg-gray-300";
  })();

  const renderSessionPrompt = () => {
    if (!sessionPrompt) return null;
    const isCompleted = sessionPrompt.status === "completed";

    return (
      <div className="shrink-0 border-t border-slate-200 bg-slate-50 px-5 py-4">
        <div className="w-full overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="flex items-start gap-3 px-5 pt-5">
            <span
              className={clsx(
                "flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border",
                isCompleted
                  ? "border-indigo-100 bg-indigo-50 text-indigo-600"
                  : "border-amber-100 bg-amber-50 text-amber-600",
              )}
            >
              {isCompleted ? <Sparkles className="h-4 w-4" /> : <History className="h-4 w-4" />}
            </span>
            <div className="min-w-0">
              <h4 className="text-sm font-semibold text-slate-900">
                {isCompleted ? "Previous result found" : "Recording already in progress"}
              </h4>
              <p className="mt-1 text-[13px] leading-relaxed text-slate-600">
                {isCompleted
                  ? "This EMR already has a finished recording. Open what it generated, or record again from scratch."
                  : "This EMR already has a recording. Carry on with it, or start over — starting over discards the old one."}
              </p>
            </div>
          </div>

          {!!sessionPrompt.transcript && (
            <div className="mx-5 mt-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                Transcript so far
              </p>
              <p className="mt-1 max-h-20 overflow-y-auto text-xs leading-relaxed text-slate-600">
                {sessionPrompt.transcript}
              </p>
            </div>
          )}

          <div className="mt-4 flex flex-col gap-2 px-5 pb-4">
            {isCompleted ? (
              <>
                <button
                  type="button"
                  onClick={promptShowPrevious}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:from-indigo-600 hover:to-violet-700 active:scale-[0.99]"
                >
                  <Sparkles className="h-4 w-4" />
                  Add more to previous recording
                </button>
                <button
                  type="button"
                  onClick={promptStartNew}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition-colors hover:border-slate-300 hover:bg-slate-50"
                >
                  <Mic className="h-4 w-4" />
                  Record New EMR
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={promptContinue}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:from-indigo-600 hover:to-violet-700 active:scale-[0.99]"
                >
                  <Play className="h-4 w-4" />
                  Continue recording
                </button>
                <button
                  type="button"
                  onClick={promptStartNew}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition-colors hover:border-slate-300 hover:bg-slate-50"
                >
                  <RotateCcw className="h-4 w-4" />
                  Start a new recording
                </button>
              </>
            )}
            <button
              type="button"
              onClick={dismissPrompt}
              className="w-full rounded-xl px-4 py-2 text-sm font-medium text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-700"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  };

  const petId = emrDetails?.pet_details?.pet_id ?? "";
  const canSearchCatalog = !!mappedClinicId && !!petId && !!emrId;

  const renderAdd = <K extends keyof VoicePreviewState>(
    key: K,
    searchType: Parameters<typeof ItemSearchAdd>[0]["searchType"],
    placeholder: string,
    toItem: (r: IEMRSectionSearchResponseData) => unknown,
  ) =>
    canSearchCatalog && itemsResultReceived ? (
      <ItemSearchAdd
        searchType={searchType}
        clinicId={mappedClinicId}
        petId={petId}
        emrId={emrId as string}
        emrType={emrDetails?.emr_type}
        placeholder={placeholder}
        toItem={toItem as never}
        onAdd={(item) => {
          setHasUserEdited(true);
          updatePreview(key, [...(preview?.[key] as unknown[]), item] as never);
        }}
      />
    ) : null;

  const hasLiveSession = phase !== "idle";

  const pendingSeconds = Math.round(pendingBytes / (VOICE_SAMPLE_RATE * 2));
  const hasBacklog = pendingSeconds >= 2;

  const READY_STATE_LABEL: Record<number, string> = {
    [ReadyState.CONNECTING]: "connecting",
    [ReadyState.OPEN]: "open",
    [ReadyState.CLOSING]: "closing",
    [ReadyState.CLOSED]: "closed",
    [ReadyState.UNINSTANTIATED]: "not started",
  };
  const debugLine = [
    `phase=${phase}`,
    `socket=${READY_STATE_LABEL[readyState] ?? readyState}`,
    connectAttempts > 1 ? `connects=${connectAttempts}` : null,
    voiceIdRef.current ? `voice=${voiceIdRef.current}` : null,
    emrId ? `emr=${emrId}` : null,
    pendingBytes > 0 ? `queued=${pendingSeconds}s` : null,
    lastFault,
  ]
    .filter(Boolean)
    .join(" · ");

  const copyDebugLine = () => {
    logVoiceEMRClick("VOICE_EMR_COPY_DEBUG_CLICKED", "Copy debug info");
    navigator.clipboard?.writeText(debugLine).then(
      () => toast.success("Debug info copied"),
      () => toast.error("Couldn't copy"),
    );
  };

  const showDebugLine = !!lastFault || (phase === "connecting" && socketUnstable);

  const renderDiscardConfirm = () => {
    if (!confirmDiscard) return null;
    return (
      <div className="shrink-0 border-t border-slate-200 bg-slate-50 px-5 py-4">
        <div className="w-full overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="flex items-start gap-3 px-5 pt-5">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-red-100 bg-red-50 text-red-600">
              <AlertTriangle className="h-4 w-4" />
            </span>
            <div className="min-w-0">
              <h4 className="text-sm font-semibold text-slate-900">Discard this recording?</h4>
              <p className="mt-1 text-[13px] leading-relaxed text-slate-600">
                The current recording and its transcript are deleted, and a fresh one starts. This
                cannot be undone.
              </p>
            </div>
          </div>

          {hasBacklog && (
            <div className="mx-5 mt-3 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-900">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>
                {formatTime(pendingSeconds)} of audio has not uploaded yet and cannot be recovered.
              </span>
            </div>
          )}

          <div className="mt-4 flex flex-col gap-2 px-5 pb-4">
            <button
              type="button"
              onClick={confirmDiscardAndRestart}
              className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-br from-red-500 to-rose-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:from-red-600 hover:to-rose-700 active:scale-[0.99]"
            >
              <Trash2 className="h-4 w-4" />
              Start New Recording
            </button>
            <button
              type="button"
              onClick={() => setConfirmDiscard(false)}
              className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition-colors hover:border-slate-300 hover:bg-slate-50"
            >
              <Mic className="h-4 w-4" />
              Continue existing recording
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderPreviewTabs = () => {
    if (!preview) return null;

    const tabs: { key: PreviewTab; label: string; ready: boolean; count?: number }[] = [
      {
        key: "clinical",
        label: "Clinical",
        ready: textResultReceived,
        count: textResultReceived ? clinicalCount : undefined,
      },
      {
        key: "orders",
        label: "Billable",
        ready: itemsResultReceived,
        count: itemsResultReceived ? orderCount : undefined,
      },
    ];

    return (
      <div className="grid grid-cols-2">
        {tabs.map((tab) => {
          const active = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => selectPreviewTab(tab.key)}
              aria-pressed={active}
              aria-current={active ? "true" : undefined}
              title={
                active
                  ? `${tab.label} — showing now`
                  : `Show ${tab.label}${tab.ready ? "" : " (still generating)"}`
              }
              className="group flex flex-col items-center gap-2 pt-1"
            >
              <span className="flex items-center gap-1.5">
                {!tab.ready && (
                  <span className="h-1.5 w-1.5 shrink-0 animate-pulse rounded-full bg-indigo-400" />
                )}
                <span
                  className={clsx(
                    "truncate text-xs transition-colors",
                    active
                      ? "font-bold text-indigo-700"
                      : "font-semibold text-slate-400 group-hover:text-slate-600",
                  )}
                >
                  {tab.label}
                </span>
                {tab.count !== undefined && tab.count > 0 && (
                  <span
                    className={clsx(
                      "rounded-full px-1.5 py-0.5 text-[10px] font-bold leading-none transition-colors",
                      active ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-400",
                    )}
                  >
                    {tab.count}
                  </span>
                )}
              </span>
              <span
                className={clsx(
                  "h-0.5 w-full rounded-full transition-colors",
                  active ? "bg-indigo-600" : "bg-slate-200 group-hover:bg-slate-300",
                )}
              />
            </button>
          );
        })}
      </div>
    );
  };

  return (
    <>
      {!isOpen && !isDismissed && (isMinimised || hasLiveSession) && (
        <button
          ref={miniRef}
          type="button"
          onPointerDown={startDrag}
          onClick={() => {
            // A drag ends with a click event — don't let it reopen the panel.
            if (movedRef.current) {
              movedRef.current = false;
              return;
            }
            onRestore?.();
          }}
          title="Open Voice to EMR — drag to move"
          aria-label="Open Voice to EMR"
          style={{ left: position.x, top: position.y, minWidth: MINI_W }}
          className={clsx(
            "fixed z-40 flex select-none items-center gap-3 rounded-2xl border-2 bg-white py-4 px-4 shadow-2xl transition-colors hover:bg-slate-50",
            isDragging ? "cursor-grabbing" : "cursor-grab",
            isRecording && !isPaused ? "border-emerald-200" : "border-gray-200",
          )}
        >
          <span className="relative flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#0B6EFF]/10">
            <Mic className="h-6 w-6 text-[#0B6EFF]" />
            <span
              className={clsx(
                "absolute -right-0.5 -top-0.5 h-3.5 w-3.5 rounded-full border-2 border-white",
                isRecording && !isPaused ? "bg-emerald-500 animate-pulse" : dotColor,
              )}
            />
          </span>

          <span className="flex flex-col items-start leading-tight">
            <span className="text-sm font-bold text-gray-900">
              {isRecording ? (isPaused ? "Paused" : "Recording") : isProcessing ? "Generating EMR" : "Voice to EMR"}
            </span>
            {(isRecording || duration > 0) && (
              <span className="font-mono text-xs tabular-nums text-gray-500">{formatTime(duration)}</span>
            )}
          </span>

          {/* Visual affordance only — the click bubbles to the pill and restores. */}
          <span
            aria-hidden="true"
            className="ml-auto flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-[#0B6EFF]/30 bg-[#0B6EFF]/10 text-[#0B6EFF]"
          >
            <Maximize2 className="h-4 w-4" />
          </span>

          {hasBacklog && (
            <span
              className={clsx(
                "rounded-full px-1.5 py-0.5 text-[10px] font-semibold",
                isSocketOpen ? "bg-blue-100 text-blue-700" : "bg-amber-100 text-amber-800",
              )}
            >
              {isSocketOpen ? "syncing" : "offline"}
            </span>
          )}
        </button>
      )}

      <aside
        ref={panelRef}
        aria-hidden={!isOpen}
        style={{
          width: PANEL_WIDTH,
          // Sizes to its content unless the host page asks for a full-height panel.
          // The cap has to account for the anchor, or a grown panel pushes its own
          // header off the top of the screen.
          height: "auto",
          ...(hasDragged
            ? {
                left: position.x,
                top: position.y,
                maxHeight: `calc(100vh - ${position.y + DOCK_GAP}px)`,
                ...(fullHeight ? { height: `calc(100vh - ${position.y + DOCK_GAP}px)` } : {}),
              }
            : fullHeight
              ? { right: DOCK_GAP, top: DOCK_GAP, bottom: DOCK_GAP }
              : {
                  right: DOCK_GAP,
                  bottom: MINI_BOTTOM_GAP,
                  maxHeight: `calc(100vh - ${MINI_BOTTOM_GAP + DOCK_GAP}px)`,
                }),
          display: isOpen ? "flex" : "none",
        }}
        className="fixed z-40 flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl"
      >
      <div
        onPointerDown={startDrag}
        role="button"
        aria-label="Drag voice panel"
        className={clsx(
          "flex items-center justify-between gap-3 select-none px-4 py-2.5 border-b border-gray-200 bg-white",
          isDragging ? "cursor-grabbing" : "cursor-grab",
        )}
      >
        <div className="flex min-w-0 flex-1 items-center gap-2">
          {/* Unadvertised toggle: the title looks and sizes exactly as before,
              but tapping it shows/hides the live transcript. Its own pointerdown
              is swallowed so the tap never starts a drag of the panel. */}
          <h3
            onClick={() => setIsTranscriptVisible((prev) => !prev)}
            onPointerDown={(event) => event.stopPropagation()}
            className="shrink-0 text-sm font-semibold text-gray-900"
          >
            Voice to EMR
          </h3>
          <span className={clsx("inline-block h-2 w-2 shrink-0 rounded-full", dotColor)} />
          <p className="truncate text-xs text-gray-500">{connectionLabel}</p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-slate-100 text-slate-500 transition-colors hover:border-slate-300 hover:bg-slate-200 hover:text-slate-700 focus:outline-none"
            onClick={minimisePanel}
            onPointerDown={(event) => event.stopPropagation()}
            title="Minimise — recording keeps running"
            aria-label="Minimise voice panel"
          >
            <Minus className="h-4 w-4" />
          </button>
        </div>
      </div>

      {inPreviewMode && preview && !appliedSummary && (
        <div className="shrink-0 border-b border-slate-200 bg-white px-5 pb-0 pt-2">
          {renderPreviewTabs()}
        </div>
      )}

      <div
        ref={previewScrollRef}
        onScroll={handlePreviewScroll}
        className="flex-1 overflow-y-auto bg-slate-50 px-5 py-3 [scrollbar-gutter:stable_both-edges]"
      >
        {!inPreviewMode && !showProcessingLoader && isTranscriptVisible && (
          <div>
            <div className="mb-2 flex items-center justify-between">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-700">Conversation</h4>
              {isRecording && (
                <span
                  className={clsx(
                    "inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest",
                    isPaused
                      ? "border-amber-200 bg-amber-50 text-amber-700"
                      : "border-emerald-200 bg-emerald-50 text-emerald-700",
                  )}
                >
                  {isPaused ? (
                    <>
                      <Pause className="h-2.5 w-2.5" />
                      Paused
                    </>
                  ) : (
                    <>
                      <span className="relative flex h-1.5 w-1.5">
                        <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                        <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
                      </span>
                      Live
                    </>
                  )}
                </span>
              )}
            </div>

            <div
              className={clsx(
                "overflow-hidden rounded-2xl border bg-white transition-colors duration-300",
                isRecording && !isPaused
                  ? "border-emerald-100 shadow-[0_1px_3px_rgba(16,185,129,0.10)]"
                  : "border-slate-200 shadow-[0_1px_3px_rgba(15,23,42,0.06)]",
              )}
            >
              {isRecording && (
                <div
                  className={clsx(
                    "flex items-center gap-2 border-b px-3.5 py-2",
                    isPaused ? "border-amber-100 bg-amber-50/70" : "border-emerald-100 bg-emerald-50/70",
                  )}
                >
                  <div className="flex h-3.5 items-end gap-[2px]" aria-hidden="true">
                    {[0, 1, 2].map((i) => (
                      <span
                        key={i}
                        className={clsx(
                          "w-[2px] origin-bottom rounded-full",
                          isPaused ? "bg-amber-400" : "bg-emerald-500 animate-voice-eq",
                        )}
                        style={{ height: `${[8, 14, 10][i]}px`, animationDelay: `${i * 130}ms` }}
                      />
                    ))}
                  </div>
                  <span
                    className={clsx(
                      "text-xs font-medium",
                      isPaused ? "text-amber-800" : "text-emerald-700",
                    )}
                  >
                    {isPaused ? "Paused" : "Listening for speech..."}
                  </span>
                </div>
              )}

              <div
                ref={transcriptScrollRef}
                className="max-h-[280px] min-h-[180px] overflow-y-auto whitespace-pre-wrap px-4 py-3.5 text-sm leading-relaxed text-slate-800 scroll-smooth"
              >
                {displayedTranscript ? (
                  <>
                    {displayedTranscript}
                    {(isRecording || displayedTranscript.length < transcript.length) && (
                      <span className="ml-0.5 inline-block h-4 w-[2px] bg-slate-700 align-middle animate-pulse" />
                    )}
                  </>
                ) : (
                  <div className="flex h-full min-h-[152px] flex-col items-center justify-center gap-2 text-center">
                    <span
                      className={clsx(
                        "flex h-9 w-9 items-center justify-center rounded-full",
                        isRecording ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-400",
                      )}
                    >
                      <Mic className="h-4 w-4" />
                    </span>
                    <p className="text-[13px] font-medium text-slate-600">
                      {isRecording ? "Waiting to hear you" : "No conversation yet"}
                    </p>
                    <p className="max-w-[240px] text-xs leading-relaxed text-slate-400">
                      {isRecording
                        ? "Speak normally — every line appears here as it is transcribed."
                        : "Tap the mic above and the transcript will appear here as you speak."}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {showProcessingLoader && (
          <div>
            <div className="mb-2 flex items-center justify-between">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-700">Result</h4>
              <span
                className={clsx(
                  "inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest",
                  generationFailed
                    ? "border-red-200 bg-red-50 text-red-700"
                    : "border-indigo-200 bg-indigo-50 text-indigo-700",
                )}
              >
                <span
                  className={clsx(
                    "inline-block h-1.5 w-1.5 rounded-full",
                    generationFailed ? "bg-red-500" : "bg-indigo-500",
                  )}
                />
                {generationFailed ? "Failed" : "Working"}
              </span>
            </div>

            <div className="flex min-h-[220px] flex-col items-center justify-center gap-4 rounded-2xl border border-slate-200 bg-white px-5 py-8 shadow-[0_1px_3px_rgba(15,23,42,0.06)]">
              <div className="relative flex h-14 w-14 items-center justify-center">
                {!generationFailed && (
                  <span className="absolute inset-0 rounded-full border-2 border-indigo-100 border-t-indigo-500 animate-spin" />
                )}
                <span
                  className={clsx(
                    "flex h-9 w-9 items-center justify-center rounded-full",
                    generationFailed ? "bg-red-50 text-red-600" : "bg-indigo-50 text-indigo-600",
                  )}
                >
                  {generationFailed ? <AlertTriangle className="h-4 w-4" /> : <Sparkles className="h-4 w-4" />}
                </span>
              </div>

              <div className="text-center">
                <div className="text-sm font-semibold text-slate-900">
                  {generationFailed ? "Couldn't generate the EMR" : "Generating your EMR…"}
                </div>
                <p className="mx-auto mt-1.5 max-w-[320px] text-xs leading-relaxed text-slate-500">
                  {generationFailed
                    ? "The recording is saved. Generating the EMR from it failed — retrying only re-runs what is missing."
                    : `AI is reading ${formatTime(duration)} of audio and filling all clinical sections. Usually 20–40 seconds.`}
                </p>
              </div>

              {!generationFailed && (
                <p className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-[11px] font-medium text-slate-400">
                  <span>Clinical notes</span>
                  <span className="text-slate-300">·</span>
                  <span>Assessment</span>
                  <span className="text-slate-300">·</span>
                  <span>Billable</span>
                </p>
              )}

              {generationFailed ? (
                <button
                  type="button"
                  onClick={retryGeneration}
                  className="inline-flex items-center gap-1.5 rounded-xl bg-indigo-600 px-4 py-2 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-indigo-700"
                >
                  <RotateCcw className="h-3.5 w-3.5" />
                  Retry generation
                </button>
              ) : (
                <p className="text-[11px] text-slate-400">
                  You can keep the panel open — this finishes on its own.
                </p>
              )}
            </div>
          </div>
        )}

        {appliedSummary && (
          <div className="flex min-h-full flex-col items-center justify-center gap-4 py-10">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100">
              <Check className="h-7 w-7 text-emerald-600" strokeWidth={2.5} />
            </span>

            <div className="text-center">
              <h4 className="text-base font-bold text-slate-900">EMR saved</h4>
              <p className="mt-1 text-xs text-slate-500">
                {appliedSummary.sections} {appliedSummary.sections === 1 ? "section" : "sections"} ·{" "}
                {formatTime(appliedSummary.seconds)} recording
              </p>
              <p className="mt-0.5 text-xs font-bold text-slate-700">
                ~{appliedSummary.minutesSaved} min of typing saved
              </p>
            </div>

            {emrGaps.length > 0 ? (
              <div className="w-full rounded-xl border border-amber-200 bg-amber-50 px-3.5 py-3">
                <p className="flex items-center gap-1.5 text-xs font-bold text-amber-800">
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                  Still empty on this EMR:
                </p>
                <div className="mt-2 space-y-2.5">
                  {emrGaps.map(({ group, fields }) => (
                    <div key={group}>
                      <p className="text-[10px] font-bold uppercase tracking-widest text-amber-600">
                        {group}
                      </p>
                      <ul className="mt-1 space-y-0.5">
                        {fields.map((field) => (
                          <li key={field} className="text-xs text-amber-800">
                            · {field}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
                <p className="mt-2.5 border-t border-amber-200 pt-2 text-[11px] leading-relaxed text-amber-700">
                  Fill these in the EMR form — the list updates as you do.
                </p>
              </div>
            ) : (
              <div className="flex w-full items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3.5 py-2.5">
                <Check className="h-3.5 w-3.5 shrink-0 text-emerald-600" />
                <p className="text-xs font-medium text-emerald-800">
                  Nothing left to fill in by hand.
                </p>
              </div>
            )}

            <button
              type="button"
              onClick={undoApply}
              disabled={isUndoing}
              title="Put the EMR back as it was and return to the review screen"
              className="group inline-flex w-full items-center justify-center gap-2 rounded-lg border border-gray-200 bg-gray-200 px-4 py-2.5 text-sm font-semibold text-gray-600 transition-colors hover:border-gray-300 hover:bg-gray-200 hover:text-gray-800 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Pencil className="h-4 w-4 shrink-0" />
              {isUndoing ? "Loading…" : "Edit to update details"}            </button>
          </div>
        )}

        {inPreviewMode && preview && !appliedSummary && (
          <div className="space-y-2">

            <div ref={clinicalPanelRef} className="space-y-2">

              <EMRSection
                title="Chief Complaint & History"
                icon={<ClipboardList />}
                loading={!textResultReceived}
                onClear={textResultReceived ? () => skipSection("history", { subjective_history: "" }) : undefined}
                isApplied={appliedSections.has("history")}
                isApplying={applyingSection === "history"}
                onApply={textResultReceived ? () => applySection("history") : undefined}
              >
                {textResultReceived ? (
                  <EMRTextarea value={preview.subjective_history} onChange={(v) => updatePreview("subjective_history", v)} placeholder="No history recorded" />
                ) : <SkeletonLines lines={4} />}
              </EMRSection>

              <EMRSection
                title="Physical Examination"
                icon={<Stethoscope />}
                loading={!textResultReceived}
                onClear={textResultReceived ? () => skipSection("physical", { physical_examinations: "" }) : undefined}
                isApplied={appliedSections.has("physical")}
                isApplying={applyingSection === "physical"}
                onApply={textResultReceived ? () => applySection("physical") : undefined}
              >
                {textResultReceived ? (
                  <>
                    <EMRTextarea value={preview.physical_examinations} onChange={(v) => updatePreview("physical_examinations", v)} placeholder="No examination notes" />
                  </>
                ) : <SkeletonLines lines={3} />}
              </EMRSection>

              <EMRSection
                title="Assessment"
                icon={<Activity />}
                loading={!textResultReceived}
                onClear={textResultReceived ? () => skipSection("assessment", { diagnosis: "", tentative_diagnosis: [], definitive_diagnosis: [], prognosis: "" }) : undefined}
                isApplied={appliedSections.has("assessment")}
                isApplying={applyingSection === "assessment"}
                onApply={textResultReceived ? () => applySection("assessment") : undefined}
              >
                {textResultReceived ? (
                  <div className="space-y-3">
                    <EMRFieldLabel label="Diagnosis">
                      <EMRTextarea value={preview.diagnosis} onChange={(v) => updatePreview("diagnosis", v)} placeholder="No diagnosis recorded" />
                    </EMRFieldLabel>
                    <DiagnosisTagInput label="Tentative Diagnosis" values={preview.tentative_diagnosis} onChange={(v) => updatePreview("tentative_diagnosis", v)} color="amber" />
                    <DiagnosisTagInput label="Definitive Diagnosis" values={preview.definitive_diagnosis} onChange={(v) => updatePreview("definitive_diagnosis", v)} color="indigo" />
                    <EMRFieldLabel label="Prognosis">
                      <EMRTextarea value={preview.prognosis} onChange={(v) => updatePreview("prognosis", v)} placeholder="—" />
                    </EMRFieldLabel>
                  </div>
                ) : <SkeletonLines lines={4} />}
              </EMRSection>

              <EMRSection
                title="Medical Instructions"
                icon={<FileText />}
                loading={!textResultReceived}
                onClear={textResultReceived ? () => skipSection("instructions", { medical_instructions: "" }) : undefined}
                isApplied={appliedSections.has("instructions")}
                isApplying={applyingSection === "instructions"}
                onApply={textResultReceived ? () => applySection("instructions") : undefined}
              >
                {textResultReceived ? (
                  <EMRTextarea
                    value={preview.medical_instructions}
                    onChange={(v) => updatePreview("medical_instructions", v)}
                    placeholder="No home-care instructions"
                  />
                ) : <SkeletonLines lines={3} />}
              </EMRSection>

              <EMRSection
                title="Follow-up"
                icon={<CalendarDays />}
                loading={!textResultReceived}
                onClear={textResultReceived ? () => skipSection("followup", { follow_up: { follow_up_comment: "", follow_up_service: "", follow_up_date: "" } }) : undefined}
                isApplied={appliedSections.has("followup")}
                isApplying={applyingSection === "followup"}
                onApply={textResultReceived ? () => applySection("followup") : undefined}
              >
                {textResultReceived ? (
                  <div className="space-y-2">
                    <EMRTextarea value={preview.follow_up.follow_up_comment} onChange={(v) => updateFollowUp("follow_up_comment", v)} placeholder="Follow-up instructions..." />
                    <div className="grid grid-cols-2 gap-2">
                      <EMRFieldLabel label="Service">
                        <input
                          type="text"
                          value={preview.follow_up.follow_up_service}
                          onChange={(e) => updateFollowUp("follow_up_service", e.target.value)}
                          placeholder="e.g. Clinic visit"
                          className={EMR_INPUT_CLASS}
                        />
                      </EMRFieldLabel>
                      <EMRFieldLabel label="Date">
                        <input
                          type="date"
                          value={preview.follow_up.follow_up_date}
                          onChange={(e) => updateFollowUp("follow_up_date", e.target.value)}
                          className={EMR_INPUT_CLASS}
                        />
                      </EMRFieldLabel>
                    </div>
                  </div>
                ) : <SkeletonLines lines={2} />}
              </EMRSection>

            </div>

            <div ref={ordersPanelRef} className="space-y-2">
              <div className="flex items-center gap-2 pt-2">
                <span className="text-sm font-semibold text-slate-700">{"Billable"}</span>
                {itemsResultReceived && orderCount > 0 && (
                  <span className="rounded-full bg-slate-200 px-1.5 py-0.5 text-[10px] font-bold leading-none text-slate-600">
                    {orderCount}
                  </span>
                )}
                <span className="h-px flex-1 bg-slate-200" />
              </div>

              <EMRSection
                title="Medications"
                icon={<Pill />}
                count={itemsResultReceived ? preview.medicines.length : undefined}
                loading={!itemsResultReceived}
                onClear={itemsResultReceived ? () => skipSection("medications", { medicines: [] }) : undefined}
                isApplied={appliedSections.has("medications")}
                isApplying={applyingSection === "medications"}
                onApply={itemsResultReceived ? () => applySection("medications") : undefined}
              >
                {itemsResultReceived ? (
                  preview.medicines.length > 0 ? (
                    <EditableItemList
                      items={preview.medicines}
                      fields={MEDICINE_FIELDS}
                      onChange={(v) => updatePreview("medicines", v)}
                      renderMeta={(m) => [m.dose && `${m.dose}${m.duration_unit ? ` ${m.duration_unit}` : ""}`, m.frequency, m.duration && `${m.duration} days`, m.timing, m.quantity && `Qty ${m.quantity}`, m.remark].filter(Boolean).join(" · ")}
                    />
                  ) : <EMREmptyState label="No medications prescribed" />
                ) : <SkeletonLines lines={3} />}
                {renderAdd("medicines", "Medicines", "Search medicines…", (r) => ({
                  name: r.variant_name ?? r.name ?? "",
                  medicine_id: r.vetic_variant_id ?? "",
                  quantity: 1,
                  prescription_type: r.prescription_type ?? "specific",
                }))}
              </EMRSection>

              <EMRSection
                title="Lab & Diagnostics"
                icon={<FlaskConical />}
                count={itemsResultReceived ? preview.lab_tests.length : undefined}
                loading={!itemsResultReceived}
                onClear={itemsResultReceived ? () => skipSection("lab", { lab_tests: [] }) : undefined}
                isApplied={appliedSections.has("lab")}
                isApplying={applyingSection === "lab"}
                onApply={itemsResultReceived ? () => applySection("lab") : undefined}
              >
                {itemsResultReceived ? (
                  preview.lab_tests.length > 0 ? (
                    <EditableItemList
                      items={preview.lab_tests}
                      fields={SIMPLE_ITEM_FIELDS}
                      onChange={(v) => updatePreview("lab_tests", v)}
                      renderMeta={(t) => [t.quantity && `Qty ${t.quantity}`, t.remark].filter(Boolean).join(" · ")}
                    />
                  ) : <EMREmptyState label="No lab tests ordered" />
                ) : <SkeletonLines lines={2} />}
                {renderAdd("lab_tests", "Lab", "Search lab tests…", (r) => ({
                  name: r.item_name ?? r.name ?? "",
                  lab_test_id: r.item_id ?? "",
                  quantity: 1,
                }))}
              </EMRSection>

              <EMRSection
                title="Treatments"
                icon={<Stethoscope />}
                count={itemsResultReceived ? (preview.treatments.length || undefined) : undefined}
                loading={!itemsResultReceived}
                onClear={itemsResultReceived ? () => skipSection("treatments", { treatments: [], treatment_instructions: "" }) : undefined}
                isApplied={appliedSections.has("treatments")}
                isApplying={applyingSection === "treatments"}
                onApply={itemsResultReceived ? () => applySection("treatments") : undefined}
              >
                {itemsResultReceived ? (
                  preview.treatments.length > 0 || preview.treatment_instructions ? (
                    <>
                      {preview.treatments.length > 0 && (
                        <EditableItemList
                          items={preview.treatments}
                          fields={SIMPLE_ITEM_FIELDS}
                          onChange={(v) => updatePreview("treatments", v)}
                          renderMeta={(t) => [t.quantity && `Qty ${t.quantity}`, t.item_tag, t.remark].filter(Boolean).join(" · ")}
                        />
                      )}
                      {preview.treatment_instructions && (
                        <div className={preview.treatments.length > 0 ? "mt-3 pt-3 border-t border-gray-100" : ""}>
                          <EMRFieldLabel label="Instructions">
                            <EMRTextarea value={preview.treatment_instructions} onChange={(v) => updatePreview("treatment_instructions", v)} />
                          </EMRFieldLabel>
                        </div>
                      )}
                    </>
                  ) : <EMREmptyState label="No treatments prescribed" />
                ) : <SkeletonLines lines={2} />}
                {renderAdd("treatments", "Treatment", "Search treatments…", (r) => ({
                  name: r.item_name ?? r.name ?? "",
                  treatment_id: r.item_id ?? "",
                  quantity: 1,
                  item_tag: r.item_tag,
                }))}
              </EMRSection>

              {itemsResultReceived && (
                <EMRSection
                  title="Deworming"
                  icon={<ShieldCheck />}
                  count={preview.deworming.length || undefined}
                  onClear={itemsResultReceived ? () => skipSection("deworming", { deworming: [] }) : undefined}
                  isApplied={appliedSections.has("deworming")}
                  isApplying={applyingSection === "deworming"}
                  onApply={itemsResultReceived ? () => applySection("deworming") : undefined}
                >
                  {preview.deworming.length > 0 ? (
                    <EditableItemList
                      items={preview.deworming}
                      fields={DEWORMING_FIELDS}
                      onChange={(v) => updatePreview("deworming", v)}
                      renderMeta={(d) => (d.quantity ? `Qty ${d.quantity}` : "")}
                    />
                  ) : (
                    <EMREmptyState label="No deworming prescribed" />
                  )}
                  {renderAdd("deworming", "Deworming", "Search deworming…", (r) => ({
                    id: r.vetic_variant_id ?? "",
                    name: r.variant_name ?? r.name ?? "",
                    quantity: 1,
                  }))}
                </EMRSection>
              )}

              {itemsResultReceived && (
                <EMRSection
                  title="Food & Diet"
                  icon={<ShoppingBasket />}
                  count={preview.food_added.length || undefined}
                  onClear={itemsResultReceived ? () => skipSection("food", { food_added: [], food_instructions: "" }) : undefined}
                  isApplied={appliedSections.has("food")}
                  isApplying={applyingSection === "food"}
                  onApply={itemsResultReceived ? () => applySection("food") : undefined}
                >
                  {preview.food_added.length === 0 && !preview.food_instructions && (
                    <EMREmptyState label="No food or diet advised" />
                  )}
                  {preview.food_added.length > 0 && (
                    <EditableItemList
                      items={preview.food_added}
                      fields={SIMPLE_ITEM_FIELDS}
                      onChange={(v) => updatePreview("food_added", v)}
                      renderMeta={(f) => [f.quantity && `Qty ${f.quantity}`, f.remark].filter(Boolean).join(" · ")}
                    />
                  )}
                  {renderAdd("food_added", "Food", "Search food & diet…", (r) => ({
                    name: r.variant_name ?? r.name ?? "",
                    product_id: r.vetic_variant_id ?? "",
                    quantity: 1,
                  }))}
                  {preview.food_instructions && (
                    <div className={preview.food_added.length > 0 ? "mt-3 pt-3 border-t border-gray-100" : ""}>
                      <EMRTextarea value={preview.food_instructions} onChange={(v) => updatePreview("food_instructions", v)} />
                    </div>
                  )}
                </EMRSection>
              )}

            </div>
          </div>
        )}
      </div>

      {!appliedSummary && (
      <div
        className={clsx(
          "shrink-0 border-t-2 bg-slate-100 px-5 py-4 shadow-[0_-6px_16px_-10px_rgba(15,23,42,0.3)] transition-colors",
          isRecording && !isPaused
            ? "border-emerald-400"
            : isPaused
              ? "border-amber-300"
              : "border-slate-200",
        )}
      >
        {hasBacklog && (
          <div
            className={clsx(
              "mb-2 w-full rounded-md border px-3 py-2 text-xs",
              isSocketOpen ? "border-blue-200 bg-blue-50 text-blue-800" : "border-amber-200 bg-amber-50 text-amber-900",
            )}
          >
            <div className="flex items-center gap-2 font-medium">
              <span
                className={clsx(
                  "inline-block h-2 w-2 rounded-full",
                  isSocketOpen ? "bg-blue-500 animate-pulse" : "bg-amber-500",
                )}
              />
              {isSocketOpen ? "Catching up..." : "Offline — still recording"}
            </div>
            <p className="mt-1 leading-relaxed">
              {formatTime(pendingSeconds)} of audio saved on this device
              {isSocketOpen ? " and uploading now." : ", waiting for the network. Keep going."}
            </p>
          </div>
        )}

        {queueFull && (
          <div className="mb-2 w-full rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
            <div className="font-medium">Offline storage full</div>
            <p className="mt-1 leading-relaxed">
              New audio is no longer being saved. Reconnect to upload what is already
              recorded, then continue.
            </p>
          </div>
        )}

        {isPaused && (
          <div className="mb-2 flex items-center justify-center gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-3 py-1.5">
            <Pause className="h-3 w-3 shrink-0 text-amber-700" />
            <span className="text-[11px] font-bold text-amber-800">Recording paused</span>
            <span className="text-[11px] text-amber-700">— tap Resume to continue</span>
          </div>
        )}

        {hasStoppedRecording ? (
          <div className="space-y-2">
            <div className="flex items-stretch gap-2">
              <div title="Length of this recording" className="flex h-9 shrink-0 items-center gap-2">
                <span className="font-mono text-2xl font-semibold leading-none tabular-nums tracking-tight text-slate-900">
                  {formatTime(duration)}
                </span>
                <div className="flex h-[17px] items-end gap-[3px]" aria-hidden="true">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <span
                      key={i}
                      className={clsx(
                        "w-[3px] origin-bottom rounded-full transition-colors duration-300",
                        isRecording && !isPaused ? "bg-emerald-500 animate-voice-eq" : "bg-slate-300",
                      )}
                      style={{ height: `${[7, 12, 17, 11, 8][i]}px`, animationDelay: `${i * 110}ms` }}
                    />
                  ))}
                </div>
              </div>
              <button
                onClick={continueRecording}
                disabled={recordDisabled}
                title={
                  isProcessing
                    ? "Wait for the EMR to finish generating"
                    : "Record more onto this same recording — the clock carries on"
                }
                className={clsx(
                  "inline-flex h-9 flex-1 items-center justify-center gap-1.5 rounded-lg border text-xs font-semibold transition-colors",
                  recordDisabled
                    ? "cursor-not-allowed border-slate-200 bg-slate-50 text-slate-400"
                    : "border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100",
                )}
              >
                <Play className="h-3.5 w-3.5" />
                Continue recording
              </button>

              <button
                onClick={requestDiscard}
                disabled={recordDisabled}
                title="Discard this recording and start a new one"
                className={clsx(
                  "inline-flex h-9 flex-1 items-center justify-center gap-1.5 rounded-lg border text-xs font-semibold transition-colors",
                  recordDisabled
                    ? "cursor-not-allowed border-slate-200 bg-slate-50 text-slate-400"
                    : "border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50",
                )}
              >
                <Mic className="h-3.5 w-3.5" />
                Start New recording
              </button>

            </div>

            <button
              onClick={applyPreview}
              disabled={!!appliedSummary || isApplying || phase !== "ready" || !!applyingSection}
              className={clsx(
                "inline-flex h-11 w-full items-center justify-center gap-1.5 rounded-lg px-4 text-sm font-semibold text-white transition",
                appliedSummary
                  ? "cursor-default bg-slate-500"
                  : isApplying || phase !== "ready" || !!applyingSection
                    ? "cursor-not-allowed bg-indigo-300"
                    : "bg-indigo-600 hover:bg-indigo-700 active:scale-[0.98]",
              )}
              title={
                appliedSummary
                  ? "Already applied — use Undo & re-edit to change it"
                  : phase !== "ready"
                    ? "Waiting for full EMR..."
                    : "Apply everything to EMR"
              }
            >
              {isApplying || applyingSection ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Check className="h-4 w-4" />
              )}
              {appliedSummary
                ? "Applied"
                : isApplying
                  ? "Generating EMR"
                  : phase === "ready"
                    ? "Apply all to EMR"
                    : "Apply all to EMR"}
              {!appliedSummary && !isApplying && phase === "ready" && filledSectionCount > 0 && (
                <span className="ml-0.5 rounded-full bg-white/20 px-1.5 py-0.5 text-[11px] font-bold leading-none tabular-nums">
                  {filledSectionCount}
                </span>
              )}
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <span className="font-mono text-2xl font-semibold tabular-nums tracking-tight text-slate-900">
                {formatTime(duration)}
              </span>
              <div className="flex h-[17px] items-end gap-[3px]" aria-hidden="true">
                {[0, 1, 2, 3, 4].map((i) => (
                  <span
                    key={i}
                    className={clsx(
                      "w-[3px] origin-bottom rounded-full transition-colors duration-300",
                      isRecording && !isPaused ? "bg-emerald-500 animate-voice-eq" : "bg-slate-200",
                    )}
                    style={{ height: `${[7, 12, 17, 11, 8][i]}px`, animationDelay: `${i * 110}ms` }}
                  />
                ))}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={requestDiscard}
                disabled={!isRecording}
                title={
                  isRecording
                    ? "Discard this recording and start over"
                    : "Available once recording has started"
                }
                className={clsx(
                  "inline-flex h-10 items-center gap-1.5 rounded-lg border px-3 text-xs font-semibold transition-colors",
                  isRecording
                    ? "border-red-200 bg-white text-red-600 hover:border-red-300 hover:bg-red-50"
                    : "cursor-not-allowed border-slate-200 bg-slate-50 text-slate-400",
                )}
              >
                <RotateCcw className="h-3.5 w-3.5" />
                Restart
              </button>

              <button
                onClick={togglePause}
                disabled={!isRecording}
                title={
                  !isRecording
                    ? "Available once recording has started"
                    : isPaused
                      ? "Resume recording"
                      : "Pause recording"
                }
                className={clsx(
                  "inline-flex h-10 items-center gap-1.5 rounded-lg border px-3 text-xs font-semibold transition-colors",
                  !isRecording
                    ? "cursor-not-allowed border-slate-200 bg-slate-50 text-slate-400"
                    : isPaused
                      ? "border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                      : "border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50",
                )}
              >
                {isPaused ? <Play className="h-3.5 w-3.5" /> : <Pause className="h-3.5 w-3.5" />}
                {isPaused ? "Resume" : "Pause"}
              </button>

              <button
                onClick={isRecording || isConnecting ? stopRecording : startRecording}
                disabled={recordDisabled}
                title={
                  isRecording
                    ? "Stop recording and generate the EMR"
                    : isConnecting
                      ? "Stop connecting"
                      : "Start recording"
                }
                className={clsx(
                  "inline-flex h-10 shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg px-4 text-xs font-semibold text-white transition-all",
                  isConnecting ? "bg-slate-500 hover:bg-slate-600" : "bg-indigo-600 hover:bg-indigo-700",
                  recordDisabled ? "cursor-not-allowed opacity-50" : "active:scale-[0.98]",
                )}
              >
                {checkingSession ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Checking…
                  </>
                ) : isConnecting ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Connecting…
                  </>
                ) : isRecording ? (
                  <>
                    <Square className="h-3.5 w-3.5 fill-current" />
                    Finish &amp; Generate EMR
                  </>
                ) : (
                  <>
                    <Mic className="h-3.5 w-3.5" />
                    Start recording
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {!isPaused && !hasStoppedRecording && statusText && (
          <p className="mt-2 text-center text-[11px] leading-relaxed text-slate-500">{statusText}</p>
        )}

        {showDebugLine && (
          <details className="group mt-2 w-full rounded-md border border-slate-200 bg-slate-50 px-2 py-1">
            <summary className="cursor-pointer list-none [&::-webkit-details-marker]:hidden">
              <span className="flex items-center gap-1.5">
                <ChevronRight className="h-3 w-3 shrink-0 text-slate-400 transition-transform group-open:rotate-90" />
                <span className="shrink-0 rounded bg-slate-200 px-1 text-[9px] font-bold uppercase tracking-wide text-slate-600">
                  Debug
                </span>
                <span className="min-w-0 flex-1 truncate font-mono text-[10px] text-slate-500">
                  {debugLine}
                </span>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    copyDebugLine();
                  }}
                  title="Copy debug info"
                  aria-label="Copy debug info"
                  className="shrink-0 rounded p-0.5 text-slate-400 transition-colors hover:bg-slate-200 hover:text-slate-600"
                >
                  <Copy className="h-3 w-3" />
                </button>
              </span>
              <span className="mt-0.5 block pl-[18px] text-[10px] leading-snug text-slate-400 group-open:hidden">
                Share this with the tech team if recording does not start.
              </span>
            </summary>
            <p className="mt-1 break-all border-t border-slate-200 pt-1 font-mono text-[10px] leading-relaxed text-slate-600">
              {debugLine}
            </p>
            <p className="mt-1 text-[10px] leading-snug text-slate-400">
              Share this with the tech team if recording does not start.
            </p>
          </details>
        )}
      </div>
      )}

      {renderSessionPrompt()}
      {renderDiscardConfirm()}

      </aside>
    </>
  );
};

const SkeletonLines = ({ lines = 3 }: { lines?: number }) => (
  <div className="space-y-2.5">
    {Array.from({ length: lines }).map((_, i) => (
      <div
        key={i}
        className="h-3 rounded-full bg-slate-100 animate-pulse"
        style={{ width: `${[95, 80, 65, 50][i] ?? 55}%` }}
      />
    ))}
  </div>
);

const EMREmptyState = ({ label }: { label: string }) => (
  <p className="py-1 text-sm italic text-slate-400">{label}</p>
);

const EMRFieldLabel = ({ label, children }: { label: string; children: ReactNode }) => (
  <div>
    <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</p>
    {children}
  </div>
);

const EMRSection = ({
  title,
  icon,
  count,
  loading = false,
  onClear,
  onApply,
  isApplying = false,
  isApplied = false,
  children,
}: {
  title: string;
  icon: ReactNode;
  count?: number;
  loading?: boolean;
  onClear?: () => void;
  onApply?: () => void;
  isApplying?: boolean;
  isApplied?: boolean;
  children: ReactNode;
}) => (
  <div
    className={clsx(
      "rounded-xl border bg-white overflow-hidden transition-all duration-300",
      loading
        ? "border-slate-100"
        : isApplied
          ? "border-emerald-200 shadow-[0_1px_3px_rgba(16,185,129,0.10)]"
          : "border-slate-200 shadow-[0_1px_3px_rgba(15,23,42,0.05)]",
    )}
  >
    <div
      className={clsx(
        "flex items-center justify-between border-b px-3 py-2 transition-colors duration-300",
        isApplied ? "border-emerald-100 bg-emerald-50/60" : "border-slate-100 bg-slate-50/60",
      )}
    >
      <div className="flex items-center gap-2 min-w-0">
        <span className={clsx("shrink-0 transition-colors duration-300 [&>svg]:h-3.5 [&>svg]:w-3.5", loading ? "text-slate-300" : isApplied ? "text-emerald-600" : "text-slate-400")}>{icon}</span>
        <span className={clsx("truncate text-[11px] font-bold uppercase tracking-widest transition-colors duration-300", loading ? "text-slate-400" : isApplied ? "text-emerald-700" : "text-slate-600")}>{title}</span>
        {isApplied && (
          <span className="text-[9px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-100 border border-emerald-200 rounded px-1.5 py-0.5 shrink-0">
            Applied
          </span>
        )}
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {loading && <span className="text-[10px] font-semibold text-indigo-400">Generating…</span>}
        {!loading && count !== undefined && count > 0 && (
          <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-indigo-500 px-1.5 text-[10px] font-bold text-white">
            {count}
          </span>
        )}

        {!loading && onApply && (
          <button
            type="button"
            onClick={onApply}
            disabled={isApplying}
            title={
              isApplied
                ? `Re-apply ${title} to EMR`
                : isApplying
                  ? "Applying…"
                  : `Apply ${title} to EMR now`
            }
            className={clsx(
              "inline-flex items-center gap-1 rounded-lg border border-indigo-100 bg-indigo-50 px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-wider text-indigo-700 transition-colors hover:border-indigo-200 hover:bg-indigo-100",
              isApplying && "cursor-not-allowed opacity-70",
            )}
            aria-label={isApplied ? `Re-apply ${title}` : `Apply ${title}`}
          >
            {isApplying && <Loader2 className="h-3 w-3 animate-spin" />}
            {isApplying ? "Applying" : isApplied ? "Re-apply" : "Apply to EMR"}
          </button>
        )}

                {!loading && onClear && (
          <button
            type="button"
            onClick={onClear}
            title={`Skip ${title}`}
            className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-gray-100 px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-wider text-gray-600 transition-colors hover:border-gray-300 hover:bg-gray-200 hover:text-gray-800"
            aria-label={`Skip ${title}`}
          >
            Skip
          </button>
        )}
      </div>
    </div>
    <div className="p-3">{children}</div>
  </div>
);

const EMRTextarea = ({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) => (
  <textarea
    value={value}
    onChange={(e) => onChange(e.target.value)}
    rows={Math.min(10, Math.max(3, value.split("\n").length + 1))}
    placeholder={placeholder}
    className={clsx(EMR_INPUT_CLASS, "min-h-[72px] resize-y py-2.5 leading-relaxed")}
  />
);

const DiagnosisTagInput = ({
  label,
  values,
  onChange,
  color,
}: {
  label: string;
  values: string[];
  onChange: (v: string[]) => void;
  color: "indigo" | "amber";
}) => {
  const [draft, setDraft] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const commit = () => {
    const t = draft.trim();
    if (!t) return;
    if (values.includes(t)) {
      setDraft("");
      return;
    }
    onChange([...values, t]);
    setDraft("");
  };
  const tagCls = color === "amber"
    ? "bg-gradient-to-b from-amber-50 to-amber-100/60 border-amber-200 text-amber-800 hover:border-amber-300"
    : "bg-gradient-to-b from-indigo-50 to-indigo-100/60 border-indigo-200 text-indigo-800 hover:border-indigo-300";
  const dotCls = color === "amber" ? "bg-amber-400" : "bg-indigo-400";
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</p>
        {values.length > 0 && (
          <span className="text-[10px] font-medium text-slate-400">
            {values.length} {values.length === 1 ? "item" : "items"}
          </span>
        )}
      </div>
      <div
        className={clsx(
          "flex min-h-[44px] flex-wrap gap-1.5 rounded-lg border bg-white px-2.5 py-1.5 transition-colors",
          isFocused
            ? "border-indigo-400 ring-2 ring-indigo-500/20"
            : "border-slate-200 hover:border-slate-300",
        )}
      >
        {values.map((v, i) => (
          <span
            key={`${v}-${i}`}
            className={clsx(
              "group inline-flex items-center gap-1.5 border text-xs rounded-full pl-2 pr-1 py-1 font-medium transition-all",
              tagCls,
            )}
          >
            <span className={clsx("w-1.5 h-1.5 rounded-full shrink-0", dotCls)} />
            <span className="truncate max-w-[220px]">{v}</span>
            <button
              type="button"
              onClick={() => onChange(values.filter((_, j) => j !== i))}
              className="rounded-full p-0.5 text-current/60 hover:text-current hover:bg-white/70 transition-colors"
              aria-label={`Remove ${v}`}
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === ",") {
              e.preventDefault();
              commit();
            } else if (e.key === "Backspace" && !draft && values.length) {
              onChange(values.slice(0, -1));
            }
          }}
          onFocus={() => setIsFocused(true)}
          onBlur={() => {
            setIsFocused(false);
            commit();
          }}
          placeholder={values.length === 0 ? "Type diagnosis and press Enter…" : "Add another…"}
          className="min-w-[140px] flex-1 border-0 bg-transparent px-1 py-1 text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-0"
        />
      </div>
      {isFocused && (
        <p className="mt-1.5 text-[10px] leading-tight text-slate-400">
          Press <kbd className="rounded border border-slate-200 bg-slate-100 px-1 py-0.5 font-mono text-[9px]">Enter</kbd> or <kbd className="rounded border border-slate-200 bg-slate-100 px-1 py-0.5 font-mono text-[9px]">,</kbd> to add · <kbd className="rounded border border-slate-200 bg-slate-100 px-1 py-0.5 font-mono text-[9px]">⌫</kbd> to remove last
        </p>
      )}
    </div>
  );
};

export default VoiceEMRRecorder;
